const styles = {
  INFO: "color: #3B82F6; font-weight: bold;",
  SUCCESS: "color: #10B981; font-weight: bold;",
  WARN: "color: #F59E0B; font-weight: bold;",
  ERROR: "color: #EF4444; font-weight: bold;"
};
const createLogEntry = (level, message, additionalData, includeStack = false) => {
  const timestamp = (/* @__PURE__ */ new Date()).toISOString();
  let logMessage;
  let logData = additionalData;
  if (typeof message === "string") {
    logMessage = message;
  } else if (typeof message === "object" && message !== null) {
    logMessage = "Object logged";
    logData = message;
  } else {
    logMessage = String(message);
  }
  const entry = {
    timestamp,
    level,
    message: logMessage
  };
  if (logData !== void 0) {
    entry.data = logData;
  }
  if (includeStack) {
    const stack = new Error().stack;
    if (stack) {
      entry.stack = stack.split("\n").slice(3).join("\n");
    }
  }
  if (typeof window !== "undefined") {
    entry.context = {
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };
  }
  return entry;
};
const logWithStyle = (consoleMethod, level, entry) => {
  console[consoleMethod](`%c[${level}]`, styles[level], entry);
};
const logger = {
  info: (message, data) => {
    const entry = createLogEntry("INFO", message, data);
    logWithStyle("log", "INFO", entry);
  },
  success: (message, data) => {
    const entry = createLogEntry("SUCCESS", message, data);
    logWithStyle("log", "SUCCESS", entry);
  },
  warn: (message, data) => {
    const entry = createLogEntry("WARN", message, data, true);
    logWithStyle("warn", "WARN", entry);
  },
  error: (message, data) => {
    const entry = createLogEntry("ERROR", message, data, true);
    logWithStyle("error", "ERROR", entry);
  }
};
function setCookie(name, value, minutes) {
  document.cookie = `${name}=${value};max-age=${minutes * 60};path=/`;
}
function getCookie(name) {
  const cookies = document.cookie.split(";");
  const foundCookie = cookies.find((cookie) => {
    const [cookieName] = cookie.split("=");
    return cookieName.trim() === name;
  });
  return foundCookie?.split("=")[1];
}
class StorageHelper {
  storage = null;
  type;
  isAvailable = false;
  constructor(type) {
    this.type = type;
    this.checkAvailability();
  }
  /**
   * Check if storage is available (handles private browsing, etc.)
   */
  checkAvailability() {
    try {
      const storage = window[this.type];
      const testKey = "__storage_test__";
      storage.setItem(testKey, "test");
      storage.removeItem(testKey);
      this.storage = storage;
      this.isAvailable = true;
    } catch (e) {
      this.isAvailable = false;
      logger.warn(`${this.type} is not available`, { error: e });
    }
  }
  /**
   * Set an item in storage
   */
  set(key, value, expiresInMinutes) {
    if (!this.isAvailable || !this.storage) return false;
    try {
      const item = { value };
      if (expiresInMinutes) {
        item.expires = Date.now() + expiresInMinutes * 60 * 1e3;
      }
      this.storage.setItem(key, JSON.stringify(item));
      return true;
    } catch (e) {
      logger.error("Failed to set storage item", { key, error: e });
      return false;
    }
  }
  /**
   * Get an item from storage
   */
  get(key) {
    if (!this.isAvailable || !this.storage) return null;
    try {
      const raw = this.storage.getItem(key);
      if (!raw) return null;
      const item = JSON.parse(raw);
      if (item.expires && Date.now() > item.expires) {
        this.remove(key);
        return null;
      }
      return item.value;
    } catch (e) {
      logger.error("Failed to get storage item", { key, error: e });
      return null;
    }
  }
  /**
   * Remove an item from storage
   */
  remove(key) {
    if (!this.isAvailable || !this.storage) return false;
    try {
      this.storage.removeItem(key);
      return true;
    } catch (e) {
      logger.error("Failed to remove storage item", { key, error: e });
      return false;
    }
  }
  /**
   * Clear all items from storage
   */
  clear() {
    if (!this.isAvailable || !this.storage) return false;
    try {
      this.storage.clear();
      return true;
    } catch (e) {
      logger.error("Failed to clear storage", { error: e });
      return false;
    }
  }
  /**
   * Check if a key exists in storage
   */
  has(key) {
    if (!this.isAvailable || !this.storage) return false;
    return this.storage.getItem(key) !== null;
  }
  /**
   * Get all keys in storage
   */
  keys() {
    if (!this.isAvailable || !this.storage) return [];
    const keys = [];
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key) keys.push(key);
    }
    return keys;
  }
  /**
   * Get storage size in bytes (approximate)
   */
  size() {
    if (!this.isAvailable || !this.storage) return 0;
    let total = 0;
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key) {
        const value = this.storage.getItem(key);
        if (value) {
          total += key.length + value.length;
        }
      }
    }
    return total;
  }
  /**
   * Create a namespaced storage helper
   * Prefixes all keys with the namespace
   */
  namespace(prefix) {
    return new NamespacedStorage(this, prefix);
  }
  /**
   * Check if storage is available
   */
  get available() {
    return this.isAvailable;
  }
}
class NamespacedStorage {
  constructor(storage, prefix) {
    this.storage = storage;
    this.prefix = prefix;
  }
  getKey(key) {
    return `${this.prefix}:${key}`;
  }
  set(key, value, expiresInMinutes) {
    return this.storage.set(this.getKey(key), value, expiresInMinutes);
  }
  get(key) {
    return this.storage.get(this.getKey(key));
  }
  remove(key) {
    return this.storage.remove(this.getKey(key));
  }
  has(key) {
    return this.storage.has(this.getKey(key));
  }
  /**
   * Clear all items in this namespace
   */
  clear() {
    const keys = this.storage.keys().filter((key) => key.startsWith(`${this.prefix}:`));
    let success = true;
    keys.forEach((key) => {
      if (!this.storage.remove(key)) {
        success = false;
      }
    });
    return success;
  }
  /**
   * Get all keys in this namespace (without prefix)
   */
  keys() {
    const prefixLength = this.prefix.length + 1;
    return this.storage.keys().filter((key) => key.startsWith(`${this.prefix}:`)).map((key) => key.substring(prefixLength));
  }
}
const local = new StorageHelper("localStorage");
const session = new StorageHelper("sessionStorage");
const CONSENT_STORAGE_KEY$2 = "cookieSelections";
const isConsentGranted = () => {
  const selections = local.get(CONSENT_STORAGE_KEY$2);
  return selections?.analytics === true;
};
const idleInject = (inject) => {
  if ("requestIdleCallback" in window) {
    requestIdleCallback(inject);
  } else {
    setTimeout(inject, 0);
  }
};
const buildGtmSrc = ({ containerId, serverUrl, scriptName }) => {
  if (serverUrl && scriptName) {
    const encodedId = btoa(`id=${containerId}`).replace(/=+$/, "");
    return `${serverUrl}/${scriptName}?P=${encodedId}&page=1`;
  }
  return `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
};
class ThemeScripts {
  /** Loads a script according to its strategy. TypeScript narrows to `GtmScriptConfig` when `id: 'gtm'`. */
  load = (config) => {
    if (config.id === "gtm") {
      this.loadGtm(config);
      return;
    }
    const { id, strategy, src, inline, async: isAsync = true, defer = false, attributes = {}, onLoad, onError } = config;
    this.applyStrategy({
      strategy,
      inject: () => this.inject({ id, src, inline, isAsync, defer, attributes, onLoad, onError })
    });
  };
  /** Handles GTM-specific setup: dataLayer push, URL building, noscript fallback. */
  loadGtm = (config) => {
    const { containerId } = config.gtmConfig;
    this.applyStrategy({
      strategy: config.strategy,
      inject: () => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ "gtm.start": (/* @__PURE__ */ new Date()).getTime(), event: "gtm.js" });
        this.inject({
          id: "gtm",
          src: buildGtmSrc(config.gtmConfig),
          isAsync: true,
          defer: false,
          attributes: { "data-gtm": containerId },
          onLoad: config.onLoad,
          onError: config.onError
        });
        if (!document.querySelector(`noscript[data-gtm="${containerId}"]`)) {
          const noscript = document.createElement("noscript");
          noscript.dataset.gtm = containerId;
          const iframe = document.createElement("iframe");
          iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
          iframe.height = "0";
          iframe.width = "0";
          iframe.style.cssText = "display:none;visibility:hidden";
          noscript.appendChild(iframe);
          document.body.insertAdjacentElement("afterbegin", noscript);
        }
      }
    });
  };
  applyStrategy = ({ strategy, inject }) => {
    switch (strategy) {
      case "immediate": {
        inject();
        break;
      }
      case "on-default-consent-set": {
        window.addEventListener("edition:consent-default-set", () => idleInject(inject), { once: true });
        break;
      }
      case "on-consent-granted": {
        window.addEventListener(
          "edition:consent-default-set",
          () => {
            if (isConsentGranted()) {
              idleInject(inject);
              return;
            }
            const waitForConsent = () => {
              if (!isConsentGranted()) return;
              idleInject(inject);
              window.removeEventListener("edition:consent-updated", waitForConsent);
            };
            window.addEventListener("edition:consent-updated", waitForConsent);
          },
          { once: true }
        );
        break;
      }
      case "idle": {
        idleInject(inject);
        break;
      }
      default: {
        const _exhaustive = strategy;
        console.warn("[ThemeScripts] Unknown strategy:", _exhaustive);
      }
    }
  };
  inject = ({ id, src, inline, isAsync, defer, attributes, onLoad, onError }) => {
    if (document.querySelector(`script[data-script-id="${id}"]`)) {
      return;
    }
    if (inline) {
      inline();
    }
    if (!src) {
      return;
    }
    const script = document.createElement("script");
    script.async = isAsync;
    script.defer = defer;
    script.src = src;
    script.dataset.scriptId = id;
    Object.entries(attributes).forEach(([key, value]) => {
      script.setAttribute(key, value);
    });
    if (onLoad) {
      script.addEventListener("load", onLoad);
    }
    if (onError) {
      script.addEventListener("error", onError);
    }
    document.head.appendChild(script);
  };
}
const LOG_STYLES = {
  header: "background:#4f46e5;color:#fff;padding:2px 8px;border-radius:3px;font-weight:bold",
  event: "background:#10b981;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;margin-left:4px",
  context: "background:#0ea5e9;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;margin-left:4px",
  consent: "background:#8b5cf6;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;margin-left:4px",
  init: "background:#f59e0b;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;margin-left:4px",
  config: "background:#6b7280;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;margin-left:4px",
  label: "color:inherit;font-weight:bold;margin-left:4px",
  muted: "color:#6b7280;font-style:italic;margin-left:4px"
};
const CATEGORY_LABELS = {
  event: "Event",
  context: "Context",
  consent: "Consent",
  init: "Init",
  config: "Config"
};
const debugLog = ({ category, label, payload }) => {
  const timestamp = (/* @__PURE__ */ new Date()).toISOString();
  const displayTime = timestamp.split("T")[1].replace("Z", "");
  const categoryStyle = LOG_STYLES[category];
  console.groupCollapsed("%cEdition DataLayer%c%s%c%s%c%s", LOG_STYLES.header, categoryStyle, ` ${CATEGORY_LABELS[category]}`, LOG_STYLES.label, ` ${label}`, LOG_STYLES.muted, ` ${displayTime}`);
  if (payload !== void 0) console.log(payload);
  console.groupEnd();
};
const toAbsoluteUrl = (url) => {
  if (!url) {
    return null;
  }
  if (url.startsWith("//")) {
    return `https:${url}`;
  }
  if (url.startsWith("/")) {
    return `${window.location.origin}${url}`;
  }
  return url;
};
const CONSENT_STORAGE_KEY$1 = "cookieSelections";
const CONSENT_DENIED_DEFAULTS = { personalization: false, marketing: false, analytics: false };
const CONSENT_KEYS = ["personalization", "marketing", "analytics"];
class CookieBanner extends HTMLElement {
  consentApiReady = false;
  async connectedCallback() {
    await this.setupConsent();
    this.addEventListeners();
    this.activateTab("consent");
    this.syncCheckboxGroups();
    if (this.dataset.forceShow === "true" || await this.shouldShowBanner()) {
      this.showBanner();
    }
  }
  /** Sets gtag consent defaults, fires `edition:consent-default-set`, and optionally loads GTM. */
  setupConsent = async () => {
    this.ensureGtag();
    const selections = local.get(CONSENT_STORAGE_KEY$1) ?? CONSENT_DENIED_DEFAULTS;
    window.gtag?.("consent", "default", {
      functionality_storage: "granted",
      analytics_storage: selections.analytics ? "granted" : "denied",
      personalization_storage: selections.personalization ? "granted" : "denied",
      ad_personalization: selections.personalization ? "granted" : "denied",
      ad_storage: selections.marketing ? "granted" : "denied",
      ad_user_data: selections.marketing ? "granted" : "denied",
      wait_for_update: 3e3
    });
    window.gtag?.("set", "ads_data_redaction", !selections.marketing);
    window.gtag?.("set", "url_passthrough", true);
    window.dispatchEvent(new CustomEvent("edition:consent-default-set"));
    await this.loadShopifyConsentApi();
  };
  addEventListeners = () => {
    this.querySelector("#tab-consent")?.addEventListener("click", () => this.activateTab("consent"));
    this.querySelector("#tab-details")?.addEventListener("click", () => this.activateTab("details"));
    this.querySelector(".allow-all-btn")?.addEventListener("click", this.handleAllowAll);
    this.querySelector(".allow-selected-btn")?.addEventListener("click", this.handleAllowSelected);
    this.querySelector('[data-action="reject"]')?.addEventListener("click", this.handleRejectAll);
    document.getElementById("manage-cookie-consent")?.addEventListener("click", (event) => {
      event.preventDefault();
      this.showBanner();
    });
  };
  activateTab = (tab) => {
    ["consent", "details"].forEach((id) => {
      this.querySelector(`#tab-${id}`)?.classList.toggle("active", id === tab);
      this.querySelector(`#panel-${id}`)?.classList.toggle("active", id === tab);
      this.querySelector(`#panel-${id}`)?.classList.toggle("tw:hidden!", id !== tab);
    });
  };
  /**
   * Returns true if the banner should be shown.
   * Prefers localStorage over the Shopify API — if the user has a stored selection we trust it
   * and skip the async API call entirely.
   */
  shouldShowBanner = async () => {
    if (local.get(CONSENT_STORAGE_KEY$1)) {
      return false;
    }
    if (!this.consentApiReady || !window.Shopify?.customerPrivacy?.currentVisitorConsent) {
      return true;
    }
    try {
      const { preferences, analytics, marketing } = await window.Shopify.customerPrivacy.currentVisitorConsent();
      return [preferences, analytics, marketing].every((v) => (v ?? "") === "");
    } catch {
      return true;
    }
  };
  loadShopifyConsentApi = () => {
    if (!window.Shopify?.loadFeatures) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      try {
        window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], (error) => {
          if (error) {
            logger.error("Shopify consent-tracking-api failed to load", error);
            resolve();
            return;
          }
          this.consentApiReady = true;
          resolve();
          document.addEventListener("visitorConsentCollected", async () => {
            try {
              const v = await window.Shopify?.customerPrivacy?.currentVisitorConsent?.();
              if (!v) {
                return;
              }
              this.setConsentGroup({ key: "personalization", checked: v.preferences === "yes" });
              this.setConsentGroup({ key: "marketing", checked: v.marketing === "yes" });
              this.setConsentGroup({ key: "analytics", checked: v.analytics === "yes" });
              this.saveSelections();
            } catch (err) {
              logger.error("visitorConsentCollected handler error", err);
            }
          });
        });
      } catch (e) {
        logger.error("Shopify loadFeatures threw", e);
        resolve();
      }
    });
  };
  /** Updates gtag consent, persists to localStorage, and fires `edition:consent-updated`. */
  applyConsent = ({ consent, hide }) => {
    window.gtag?.("consent", "update", {
      functionality_storage: "granted",
      analytics_storage: consent.analytics === "yes" ? "granted" : "denied",
      personalization_storage: consent.preferences === "yes" ? "granted" : "denied",
      ad_personalization: consent.preferences === "yes" ? "granted" : "denied",
      ad_storage: consent.marketing === "yes" ? "granted" : "denied",
      ad_user_data: consent.marketing === "yes" ? "granted" : "denied"
    });
    window.gtag?.("set", "ads_data_redaction", consent.marketing !== "yes");
    this.saveSelections();
    window.dispatchEvent(new CustomEvent("edition:consent-updated"));
    window.Shopify?.customerPrivacy?.setTrackingConsent?.({
      analytics: consent.analytics === "yes",
      marketing: consent.marketing === "yes",
      preferences: consent.preferences === "yes"
    });
    if (hide) {
      this.hideBanner();
    }
  };
  handleAllowAll = () => {
    this.querySelectorAll("input.ed-checkbox[data-consent]").forEach((cb) => {
      if (!cb.disabled) {
        cb.checked = true;
      }
    });
    this.applyConsent({ consent: { preferences: "yes", marketing: "yes", analytics: "yes" }, hide: true });
  };
  handleAllowSelected = () => {
    const { personalization, marketing, analytics } = this.getSelections();
    this.applyConsent({
      consent: {
        preferences: personalization ? "yes" : "no",
        marketing: marketing ? "yes" : "no",
        analytics: analytics ? "yes" : "no"
      },
      hide: true
    });
  };
  handleRejectAll = () => {
    this.applyConsent({ consent: { preferences: "no", marketing: "no", analytics: "no" }, hide: true });
  };
  /** Keeps the consent/details tab panels in sync — the same consent key can appear in both tabs. */
  syncCheckboxGroups = () => {
    const checkboxes = Array.from(this.querySelectorAll("input.ed-checkbox[data-consent]"));
    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", ({ currentTarget }) => {
        const source = currentTarget;
        checkboxes.forEach((other) => {
          if (other !== source && other.dataset.consent === source.dataset.consent && !other.disabled) {
            other.checked = source.checked;
          }
        });
      });
    });
    const grouped = checkboxes.reduce((acc, cb) => {
      const key = cb.dataset.consent;
      (acc[key] ??= []).push(cb);
      return acc;
    }, {});
    Object.values(grouped).forEach(([source, ...rest]) => {
      rest.forEach((cb) => {
        if (!cb.disabled) {
          cb.checked = source.checked;
        }
      });
    });
  };
  setConsentGroup = ({ key, checked }) => {
    this.querySelectorAll(`input.ed-checkbox[data-consent="${key}"]`).forEach((cb) => {
      if (!cb.disabled) {
        cb.checked = checked;
      }
    });
  };
  saveSelections = () => {
    local.set(CONSENT_STORAGE_KEY$1, this.getSelections());
  };
  getSelections = () => CONSENT_KEYS.reduce(
    (acc, key) => ({
      ...acc,
      [key]: !!this.querySelector(`input.ed-checkbox[data-consent="${key}"]`)?.checked
    }),
    { personalization: false, marketing: false, analytics: false }
  );
  get overlay() {
    return this.querySelector("[data-cookie-overlay]") ?? this;
  }
  /**
   * Opens the banner. Applies any previously saved selections to the checkboxes first —
   * if nothing is stored, the HTML defaults remain as-is.
   * Can be triggered externally via `#manage-cookie-consent`.
   */
  showBanner = () => {
    const saved = local.get(CONSENT_STORAGE_KEY$1);
    if (saved) {
      CONSENT_KEYS.forEach((key) => this.setConsentGroup({ key, checked: saved[key] }));
    }
    this.overlay.removeAttribute("hidden");
    this.overlay.classList.remove("tw:hidden!");
  };
  hideBanner = () => {
    this.overlay.classList.add("tw:hidden!");
  };
  /** Creates a `window.gtag` shim that queues calls into `dataLayer` if the real gtag hasn't loaded yet. */
  ensureGtag = () => {
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };
    }
  };
}
const sha256 = async (value) => {
  if (!value) {
    return null;
  }
  try {
    const encoded = new TextEncoder().encode(value);
    const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
};
const resolveUrl = (input) => {
  if (typeof input === "string") {
    return input;
  }
  if (input instanceof URL) {
    return input.pathname;
  }
  return input.url;
};
const activeCurrency = () => window.Shopify?.currency?.active ?? "";
const parseRequestQuantity = (init) => {
  const body = init?.body;
  if (!body) {
    return 1;
  }
  try {
    if (typeof body === "string") {
      const parsed = JSON.parse(body);
      return Number(parsed.quantity) || 1;
    }
    if (body instanceof URLSearchParams) {
      return Number(body.get("quantity")) || 1;
    }
    if (body instanceof FormData) {
      return Number(body.get("quantity")) || 1;
    }
  } catch {
  }
  return 1;
};
const buildCartItem = (item, quantity) => ({
  item_name: item.product_title,
  item_id: String(item.product_id),
  item_brand: item.vendor,
  item_type: item.product_type,
  item_variant: String(item.variant_id),
  item_variant_name: item.variant_title,
  item_image: toAbsoluteUrl(item.featured_image?.url),
  item_url: toAbsoluteUrl(item.url),
  price: item.price / 100,
  sku: item.sku,
  quantity
});
const diffCart = ({ snapshot, newCart, onAddToCart, onRemoveFromCart }) => {
  const currency = activeCurrency();
  const snapshotMap = new Map(snapshot.items.map((item) => [item.key, item]));
  const newMap = new Map(newCart.items.map((item) => [item.key, item]));
  snapshotMap.forEach((oldItem, key) => {
    const newQty = newMap.get(key)?.quantity ?? 0;
    if (newQty < oldItem.quantity) {
      const qtyRemoved = oldItem.quantity - newQty;
      onRemoveFromCart({
        event: "edition_remove_from_cart",
        ecommerce: {
          timestamp: (/* @__PURE__ */ new Date()).toISOString(),
          url: window.location.href,
          currency,
          value: oldItem.price / 100 * qtyRemoved,
          items: [buildCartItem(oldItem, qtyRemoved)]
        }
      });
    }
  });
  newMap.forEach((newItem, key) => {
    const oldQty = snapshotMap.get(key)?.quantity ?? 0;
    if (newItem.quantity > oldQty) {
      const qtyAdded = newItem.quantity - oldQty;
      onAddToCart({
        event: "edition_add_to_cart",
        ecommerce: {
          timestamp: (/* @__PURE__ */ new Date()).toISOString(),
          url: window.location.href,
          currency,
          value: newItem.price / 100 * qtyAdded,
          items: [buildCartItem(newItem, qtyAdded)]
        }
      });
    }
  });
};
const initCartInterceptor = ({ onAddToCart, onRemoveFromCart }) => {
  const originalFetch = window.fetch.bind(window);
  const fetchCart = () => originalFetch("/cart.json").then((r) => r.json());
  window.fetch = async (input, init) => {
    const url = resolveUrl(input);
    if (url.includes("/cart/add")) {
      const response = await originalFetch(input, init);
      const addedQuantity = parseRequestQuantity(init);
      response.clone().json().then((data) => {
        const items = "items" in data ? data.items : [data];
        const currency = activeCurrency();
        items.forEach((item) => {
          onAddToCart({
            event: "edition_add_to_cart",
            ecommerce: {
              timestamp: (/* @__PURE__ */ new Date()).toISOString(),
              url: window.location.href,
              currency,
              value: item.price / 100 * addedQuantity,
              items: [
                {
                  item_name: item.product_title,
                  item_id: String(item.id),
                  item_brand: item.vendor ?? null,
                  item_type: item.product_type,
                  item_variant: String(item.id),
                  item_variant_name: item.variant_title,
                  item_image: item.featured_image?.url ?? null,
                  item_url: item.url,
                  price: item.price / 100,
                  sku: item.sku ?? null,
                  quantity: addedQuantity
                }
              ]
            }
          });
        });
      }).catch(() => {
      });
      return response;
    }
    if (url.includes("/cart/update") || url.includes("/cart/change")) {
      const snapshot = await fetchCart();
      const response = await originalFetch(input, init);
      const newCart = await fetchCart();
      diffCart({ snapshot, newCart, onAddToCart, onRemoveFromCart });
      return response;
    }
    return originalFetch(input, init);
  };
};
const DEBUG_MODE = true;
const CONSENT_STORAGE_KEY = "cookieSelections";
const DL_QUEUE = { KEY: "dl_pending_queue", TTL_MINUTES: 30, MAX_SIZE: 20 };
const PAGE_DATA_DEFAULTS = {
  pageType: "other",
  product: null,
  collection: null,
  searchQuery: null,
  customer: null
};
class DataLayer {
  /**
   * Registers the two window event listeners that drive the lifecycle.
   * Must be called once from `app.ts` — safe to call before `CookieBanner` connects.
   *
   * - `edition:consent-default-set` → runs full initialisation (once).
   * - `edition:consent-updated`     → flushes the session queue.
   */
  init = () => {
    window.addEventListener(
      "edition:consent-default-set",
      () => {
        this.initialize().catch((err) => logger.error("DataLayer init failed", err));
      },
      { once: true }
    );
    window.addEventListener("edition:consent-updated", () => {
      this.flushQueue();
    });
  };
  /** Runs the full initialisation sequence — pushes context, user, and page events then wires up interceptors. */
  initialize = async () => {
    window.dataLayer = window.dataLayer || [];
    const pageData = this.readPageData();
    this.pushLocation();
    this.pushContextData({ pageType: pageData.pageType });
    this.pushDeviceData();
    if (pageData.customer) {
      await this.pushUserData({ customer: pageData.customer });
    }
    await this.pushPageTypeEvent({ pageData });
    initCartInterceptor({
      onAddToCart: (payload) => this.pushToDataLayer({ payload, consentRequirement: "analytics" }),
      onRemoveFromCart: (payload) => this.pushToDataLayer({ payload, consentRequirement: "analytics" })
    });
    this.bindFormListeners();
    window.editionDataLayer = {
      trackCartView: this.trackCartView,
      trackAddToCart: this.trackAddToCart,
      trackRemoveFromCart: this.trackRemoveFromCart
    };
    {
      debugLog({ category: "init", label: "DataLayer initialized", payload: { pageType: pageData.pageType } });
    }
  };
  /** Parses the `#datalayer-data` JSON script tag injected by `datalayer-data.liquid`. Returns safe defaults on failure. */
  readPageData = () => {
    const el = document.getElementById("datalayer-data");
    if (!el?.textContent) {
      return PAGE_DATA_DEFAULTS;
    }
    try {
      return JSON.parse(el.textContent);
    } catch {
      logger.warn("Failed to parse #datalayer-data JSON");
      return PAGE_DATA_DEFAULTS;
    }
  };
  /**
   * Pushes a payload to `window.dataLayer` if the required consent is granted,
   * otherwise holds it in `sessionStorage` for later flushing.
   */
  pushToDataLayer = ({ payload, consentRequirement }) => {
    const isEvent = payload.event.startsWith("edition_");
    if (this.checkConsent({ requirement: consentRequirement })) {
      window.dataLayer.push(payload);
      {
        debugLog({ category: isEvent ? "event" : "context", label: payload.event, payload });
      }
    } else {
      this.queueEvent({ payload, consentRequirement });
      {
        debugLog({ category: "config", label: `Queued (no consent): ${payload.event}`, payload });
      }
    }
  };
  /** Reads consent state from localStorage — same key written by `CookieBanner`. */
  checkConsent = ({ requirement }) => {
    if (requirement === "none") {
      return true;
    }
    const selections = local.get(CONSENT_STORAGE_KEY);
    if (!selections) {
      return false;
    }
    if (requirement === "analytics") {
      return selections.analytics;
    }
    if (requirement === "marketing") {
      return selections.marketing;
    }
    return false;
  };
  /** Appends an event to the queue, dropping the oldest if the cap is reached. Persists for 30 minutes across page loads. */
  queueEvent = (event) => {
    const queue = this.getQueue();
    if (queue.length >= DL_QUEUE.MAX_SIZE) {
      queue.shift();
    }
    queue.push(event);
    local.set(DL_QUEUE.KEY, queue, DL_QUEUE.TTL_MINUTES);
  };
  /** Returns the current queue from localStorage, or an empty array if nothing is queued or TTL has expired. */
  getQueue = () => local.get(DL_QUEUE.KEY) ?? [];
  /**
   * Replays all queued events that now have consent, leaving the rest in place.
   * Called automatically on `edition:consent-updated`.
   */
  flushQueue = () => {
    const queue = this.getQueue();
    if (!queue.length) {
      return;
    }
    const remaining = [];
    queue.forEach(({ payload, consentRequirement }) => {
      if (this.checkConsent({ requirement: consentRequirement })) {
        window.dataLayer.push(payload);
        {
          debugLog({ category: "event", label: `Flushed: ${payload.event}`, payload });
        }
      } else {
        remaining.push({ payload, consentRequirement });
      }
    });
    local.set(DL_QUEUE.KEY, remaining, DL_QUEUE.TTL_MINUTES);
    const flushed = queue.length - remaining.length;
    if (flushed > 0 && DEBUG_MODE) {
      debugLog({ category: "init", label: `Flushed ${flushed} queued events`, payload: { remaining: remaining.length } });
    }
  };
  /** Pushes the current page URL breakdown and shop domain. No consent required. */
  pushLocation = () => {
    const { hash, host, hostname, href, origin, pathname, port, protocol, search } = window.location;
    this.pushToDataLayer({
      payload: {
        event: "location",
        location: { hash, host, hostname, href, origin, pathname, port, protocol, search },
        shop: window.Shopify?.shop ?? null
      },
      consentRequirement: "none"
    });
  };
  /** Pushes page type, title, and URL. No consent required. */
  pushContextData = ({ pageType }) => {
    this.pushToDataLayer({
      payload: {
        event: "contextData",
        page: { type: pageType, title: document.title, url: window.location.href }
      },
      consentRequirement: "none"
    });
  };
  /** Pushes browser/device signals — language, screen size, user agent, cookie state. No consent required. */
  pushDeviceData = () => {
    this.pushToDataLayer({
      payload: {
        event: "deviceData",
        encoding: document.characterSet,
        language: navigator.language,
        cookieEnabled: navigator.cookieEnabled,
        screen: `${window.innerWidth}x${window.innerHeight}`,
        userAgent: navigator.userAgent
      },
      consentRequirement: "none"
    });
  };
  /** Hashes PII fields with SHA-256 before pushing — email, phone, first/last name. */
  pushUserData = async ({ customer }) => {
    const [sha256Email, sha256Phone, sha256FirstName, sha256LastName] = await Promise.all([
      customer.email ? sha256(customer.email.toLowerCase().trim()) : null,
      customer.phone ? sha256(customer.phone.replace(/\D/g, "")) : null,
      customer.first_name ? sha256(customer.first_name.toLowerCase().trim()) : null,
      customer.last_name ? sha256(customer.last_name.toLowerCase().trim()) : null
    ]);
    this.pushToDataLayer({
      payload: {
        event: "userData",
        customer: {
          email: customer.email ?? null,
          phone_number: customer.phone ?? null,
          sha256_email_address: sha256Email,
          sha256_phone_number: sha256Phone,
          address: customer.first_name ?? customer.last_name ? {
            first_name: customer.first_name ?? null,
            last_name: customer.last_name ?? null,
            sha256_first_name: sha256FirstName,
            sha256_last_name: sha256LastName
          } : null
        }
      },
      consentRequirement: "analytics"
    });
  };
  /**
   * Dispatches the page-type-specific event based on `pageData.pageType`.
   * For `search`, reads `?q` from the URL rather than relying on Liquid output.
   */
  pushPageTypeEvent = async ({ pageData }) => {
    switch (pageData.pageType) {
      case "product": {
        if (pageData.product) {
          this.pushViewItem({ product: pageData.product });
        }
        break;
      }
      case "collection": {
        if (pageData.collection) {
          this.pushCollectionView({ collection: pageData.collection });
        }
        break;
      }
      case "search": {
        const query = new URLSearchParams(window.location.search).get("q") ?? pageData.searchQuery;
        if (query) {
          this.pushSearch({ query });
        }
        break;
      }
      case "cart": {
        await this.trackCartView();
        break;
      }
      case "other": {
        break;
      }
      default: {
        const _exhaustive = pageData.pageType;
        logger.warn("Unhandled DataLayer page type", _exhaustive);
      }
    }
  };
  /** Pushes `edition_view_item` using the URL-selected variant, falling back to the first variant. Requires analytics consent. */
  pushViewItem = ({ product }) => {
    const urlVariantId = new URLSearchParams(window.location.search).get("variant");
    const variant = (urlVariantId ? product.variants.find((v) => String(v.id) === urlVariantId) : null) ?? product.variants[0];
    if (!variant) {
      return;
    }
    this.pushToDataLayer({
      payload: {
        event: "edition_view_item",
        ecommerce: {
          timestamp: (/* @__PURE__ */ new Date()).toISOString(),
          url: window.location.href,
          currency: window.Shopify?.currency?.active ?? "",
          value: variant.price / 100,
          items: [
            {
              item_name: product.title,
              item_id: String(product.id),
              item_brand: product.vendor,
              item_type: product.type,
              item_variant: String(variant.id),
              item_variant_name: variant.title,
              item_image: toAbsoluteUrl(variant.featured_image?.src ?? product.featured_image),
              item_url: toAbsoluteUrl(`/products/${product.handle}`),
              price: variant.price / 100,
              sku: variant.sku ?? null,
              quantity: 1
            }
          ]
        }
      },
      consentRequirement: "analytics"
    });
  };
  /** Pushes `edition_collection_view` with all currently-rendered products (first 30 from Liquid). Requires analytics consent. */
  pushCollectionView = ({ collection }) => {
    const { title, handle, products } = collection;
    this.pushToDataLayer({
      payload: {
        event: "edition_collection_view",
        ecommerce: {
          timestamp: (/* @__PURE__ */ new Date()).toISOString(),
          url: window.location.href,
          currency: window.Shopify?.currency?.active ?? "",
          value: 1,
          item_list_name: title,
          item_list_id: handle,
          item_list_products_count: products.length,
          items: products.flatMap(
            (p) => p.variants.map(
              (v) => ({
                item_name: p.title,
                item_id: String(p.id),
                item_brand: p.vendor,
                item_type: p.type,
                item_variant: String(v.id),
                item_variant_name: v.title,
                item_image: toAbsoluteUrl(v.featured_image?.src ?? p.featured_image),
                item_url: toAbsoluteUrl(`/products/${p.handle}`),
                price: v.price / 100,
                sku: v.sku ?? null
              })
            )
          )
        }
      },
      consentRequirement: "analytics"
    });
  };
  /** Pushes `edition_search` with the query string only — no result products. Requires analytics consent. */
  pushSearch = ({ query }) => {
    this.pushToDataLayer({
      payload: {
        event: "edition_search",
        search_term: query,
        ecommerce: { timestamp: (/* @__PURE__ */ new Date()).toISOString(), url: window.location.href }
      },
      consentRequirement: "analytics"
    });
  };
  /** Fetches `/cart.json` and pushes `edition_cart_view`. Call this when a cart drawer opens. */
  trackCartView = async () => {
    try {
      const response = await fetch("/cart.json");
      const cart = await response.json();
      this.pushToDataLayer({
        payload: {
          event: "edition_cart_view",
          ecommerce: {
            timestamp: (/* @__PURE__ */ new Date()).toISOString(),
            url: window.location.href,
            currency: cart.currency,
            value: cart.total_price / 100,
            items: cart.items.map(
              (item) => ({
                item_name: item.product_title,
                item_id: String(item.product_id),
                item_brand: item.vendor,
                item_type: item.product_type,
                item_variant: String(item.variant_id),
                item_variant_name: item.variant_title,
                item_image: toAbsoluteUrl(item.featured_image?.url),
                item_url: toAbsoluteUrl(item.url),
                price: item.price / 100,
                sku: item.sku,
                quantity: item.quantity
              })
            )
          }
        },
        consentRequirement: "analytics"
      });
    } catch (err) {
      logger.error("DataLayer: failed to fetch cart", err);
    }
  };
  /** Manually push an `edition_add_to_cart` event — use when a custom flow bypasses `/cart/add.js`. */
  trackAddToCart = (payload) => {
    this.pushToDataLayer({ payload, consentRequirement: "analytics" });
  };
  /** Manually push an `edition_remove_from_cart` event — use when a custom flow bypasses `/cart/update.js`. */
  trackRemoveFromCart = (payload) => {
    this.pushToDataLayer({ payload, consentRequirement: "analytics" });
  };
  /** Listens for contact form submits on `document` and distinguishes newsletter vs contact events by form fields. */
  bindFormListeners = () => {
    document.addEventListener("submit", (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) {
        return;
      }
      const action = form.getAttribute("action") ?? "";
      if (!action.includes("/contact")) {
        return;
      }
      const elements = Array.from(form.elements).filter((el) => el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement);
      const getField = (name) => elements.find((el) => el.name === name)?.value ?? null;
      const isNewsletter = elements.some((el) => el.name === "contact[tags]" && el instanceof HTMLInputElement && el.value === "newsletter");
      if (isNewsletter) {
        this.pushToDataLayer({
          payload: {
            event: "edition_newsletter_subscribe",
            timestamp: (/* @__PURE__ */ new Date()).toISOString(),
            customer_email: getField("contact[email]")
          },
          consentRequirement: "analytics"
        });
      } else {
        this.pushToDataLayer({
          payload: {
            event: "edition_contact_form_submit",
            timestamp: (/* @__PURE__ */ new Date()).toISOString(),
            customer_name: getField("contact[Name]"),
            customer_email: getField("contact[email]"),
            customer_phone: getField("contact[Phone number]"),
            comment: getField("contact[Comment]")
          },
          consentRequirement: "analytics"
        });
      }
    });
  };
}
class Geolocator extends HTMLElement {
  geolocatorScript;
  countryDataArray;
  geolocatorContentTemplate;
  geolocatorContentPlaceholder;
  modal;
  closeModalButton;
  closeCountryListModalButton;
  countryModalToggle;
  countryListModal;
  countryListModalToggles;
  countryListButtons;
  stayButton;
  leaveButton;
  overlay;
  constructor() {
    super();
    this.getBrowsingContext = this.getBrowsingContext.bind(this);
  }
  async connectedCallback() {
    this.initDataAndElements();
    this.addEventListeners();
    this.handleGeolocationCookie();
    const isCookied = getCookie("geolocation");
    if (isCookied) {
      console.log("Geolocation cookie is set. Geolocator will not run.");
      return;
    }
    const browsingContext = await this.getBrowsingContext();
    const activeCountry = this.getActiveCountryAndLanguage();
    const recommendedMatch = this.getRecommendedMatch(browsingContext);
    if (recommendedMatch?.handle === activeCountry.country) {
      console.log("Recommended match is the same as the active country. Geolocator will not run.");
      return;
    }
    this.toggleGeolocator();
    this.setupGeolocatorContent(recommendedMatch);
  }
  handleGeolocationCookie() {
    const urlParams = new URLSearchParams(window.location.search);
    const geolocation = urlParams.get("geolocation");
    if (geolocation) {
      setCookie("geolocation", geolocation, 30);
    }
  }
  toggleGeolocator() {
    this.classList.toggle("tw:hidden");
    this.setAttribute("geolocation-modal-active", this.getAttribute("geolocation-modal-active") === "true" ? "false" : "true");
    setTimeout(() => {
      this.modal.classList.toggle("active");
    }, 300);
  }
  setupGeolocatorContent(recommendedMatch) {
    const geolocatorContent = this.geolocatorContentTemplate.content.cloneNode(true);
    const content = geolocatorContent.querySelector(`[data-id="${recommendedMatch.id}"]`);
    if (content) {
      this.geolocatorContentPlaceholder.appendChild(content);
    }
    this.countryModalToggle = this.querySelector(".country-modal-toggle");
    this.stayButton = this.querySelector(".stay-button");
    this.leaveButton = this.querySelector(".leave-button");
    this.countryModalToggle.addEventListener("click", (event) => {
      event.preventDefault();
      this.toggleCountryListModal(this.countryModalToggle);
    });
    this.stayButton.addEventListener("click", (event) => {
      event.preventDefault();
      setCookie("geolocation", recommendedMatch.handle, 30);
      this.toggleGeolocator();
    });
    this.leaveButton.addEventListener("click", (event) => {
      event.preventDefault();
      const { locale, handle, type, url } = this.leaveButton.dataset;
      setCookie("geolocation", handle, 30);
      const isMarket = type === "market";
      if (!isMarket) {
        if (url) {
          window.location.href = url;
        } else {
          window.location.href = `${window.location.href}?geolocation=${handle}`;
        }
        return;
      }
      this.handleMarketSwitch(handle, locale);
    });
  }
  toggleCountryListModal(trigger) {
    const geoLocatorActive = this.getAttribute("geolocation-modal-active") === "true";
    const isInModalTrigger = trigger.dataset.inmodaltoggle === "true";
    if (isInModalTrigger && !geoLocatorActive) {
      this.classList.toggle("tw:hidden");
      this.countryListModal.classList.toggle("active");
      return;
    }
    if (isInModalTrigger) {
      this.modal.classList.toggle("active");
      this.countryListModal.classList.toggle("active");
    } else {
      this.classList.toggle("tw:hidden");
      this.countryListModal.classList.add("active");
    }
  }
  initDataAndElements() {
    this.modal = this.querySelector("#geolocator-modal");
    this.closeModalButton = this.querySelector("#close-geolocator-modal");
    this.geolocatorScript = this.querySelector("#geolocator-script");
    this.countryDataArray = JSON.parse(this.geolocatorScript.innerHTML);
    this.geolocatorContentTemplate = this.querySelector("#geolocator-content-tempalte");
    this.geolocatorContentPlaceholder = this.querySelector("#geolocator-content-placeholder");
    this.countryListModal = this.querySelector("#geolocator-countrylist-modal");
    this.closeCountryListModalButton = this.querySelector("#close-geolocator-countrylist-modal");
    this.countryListButtons = Array.from(this.querySelectorAll(".countrylist-button"));
    this.countryListModalToggles = Array.from(document.querySelectorAll(".country-modal-toggle"));
    this.overlay = this.querySelector(".geolocator-overlay");
  }
  addEventListeners() {
    this.closeModalButton?.addEventListener("click", (event) => {
      event.preventDefault();
      this.toggleGeolocator();
    });
    this.closeCountryListModalButton.addEventListener("click", (event) => {
      event.preventDefault();
      this.toggleCountryListModal(this.closeCountryListModalButton);
    });
    this.countryListButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        this.handleCountryListButtonClick(button);
      });
    });
    this.countryListModalToggles.forEach((toggle) => {
      toggle?.addEventListener("click", (event) => {
        event.preventDefault();
        this.toggleCountryListModal(toggle);
      });
    });
  }
  handleCountryListButtonClick(button) {
    setCookie("geolocation", button.dataset.handle, 30);
    const isMarket = button.dataset.type === "market";
    if (!isMarket) {
      const url = button.dataset.url;
      if (url) {
        window.location.href = url;
      } else {
        window.location.href = `${window.location.href}?geolocation=${button.dataset.handle}`;
      }
      return;
    }
    const { locale, handle } = button.dataset;
    this.handleMarketSwitch(handle, locale);
  }
  handleMarketSwitch(handle, locale) {
    const formId = crypto.randomUUID();
    const formElement = document.createElement("form");
    formElement.id = formId;
    formElement.action = "/localization";
    formElement.method = "POST";
    formElement.hidden = true;
    formElement.innerHTML = `
      <input name="_method" value="PUT">
      <input name="country_code" value="${handle}">
      <input name="language_code" value="${locale}">
      <input name="return_to" value="${window.location.href}?geolocation=${handle}">
    `;
    document.body.appendChild(formElement);
    formElement.submit();
  }
  getActiveCountryAndLanguage() {
    const { country, locale } = window.Shopify;
    return {
      country,
      locale
    };
  }
  getRecommendedMatch(browsingContext) {
    if (!browsingContext) return;
    const { suggestedCountry, suggestedLanguage, detectedCountry } = browsingContext;
    const countryHandle = suggestedCountry?.handle || detectedCountry?.handle;
    const languageHandle = suggestedLanguage?.handle;
    if (!countryHandle) return;
    const parseCountries = (countriesStr) => {
      if (!countriesStr) return [];
      return countriesStr.split(",").map((c) => c.trim().toUpperCase()).filter((c) => c.length > 0);
    };
    const isCountryInMarket = (market, country) => {
      const countries = parseCountries(market.countries || "");
      if (countries.length === 0) {
        return market.handle.toUpperCase() === country.toUpperCase();
      }
      return countries.includes(country.toUpperCase());
    };
    let recommendedMatch = this.countryDataArray.find((market) => isCountryInMarket(market, countryHandle) && market.locale === languageHandle);
    if (!recommendedMatch) {
      recommendedMatch = this.countryDataArray.find((market) => isCountryInMarket(market, countryHandle));
    }
    if (!recommendedMatch) {
      recommendedMatch = this.countryDataArray.find((market) => market.primary);
    }
    return recommendedMatch;
  }
  async getBrowsingContext() {
    const response = await fetch("/browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true");
    const data = await response.json();
    const detectedValues = data.detected_values;
    const { handle, name } = detectedValues.country;
    const { suggestions } = data;
    let suggestedCountry = null;
    let suggestedLanguage = null;
    if (suggestions.length) {
      const suggestion = suggestions.find((_suggestion) => _suggestion.parts.country.handle === detectedValues.country.handle);
      suggestedCountry = suggestion?.parts.country;
      suggestedLanguage = suggestion?.parts.language;
    }
    const browsingContext = {
      detectedCountry: {
        handle,
        name
      },
      suggestedCountry,
      suggestedLanguage
    };
    return browsingContext;
  }
}
const SCOPE_CHANGE_EVENT$2 = "header-menu-scope-change";
const STORAGE_KEY$2 = "header-active-menu";
class HeaderMenuButtons extends HTMLElement {
  activeMenu = "default";
  onClick;
  onScopeChange;
  onLogoClick;
  constructor() {
    super();
    this.onClick = this.handleClick.bind(this);
    this.onScopeChange = this.handleScopeChange.bind(this);
    this.onLogoClick = this.handleLogoClick.bind(this);
  }
  connectedCallback() {
    this.addEventListener("click", this.onClick);
    document.addEventListener(SCOPE_CHANGE_EVENT$2, this.onScopeChange);
    document.addEventListener("click", this.onLogoClick);
    const stored = session.get(STORAGE_KEY$2);
    if (stored && stored !== "default") {
      this.activeMenu = stored;
    }
    this.syncUi();
  }
  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
    document.removeEventListener(SCOPE_CHANGE_EVENT$2, this.onScopeChange);
    document.removeEventListener("click", this.onLogoClick);
  }
  handleClick(event) {
    const target = event.target?.closest("[data-menu-button]");
    if (!target) return;
    event.preventDefault();
    const nextMenu = target.dataset.menuButton;
    if (!nextMenu) return;
    const newScope = this.activeMenu === nextMenu ? "default" : nextMenu;
    document.dispatchEvent(new CustomEvent(SCOPE_CHANGE_EVENT$2, { detail: { scope: newScope } }));
  }
  handleLogoClick(event) {
    if (!event.target?.closest(".logolink")) return;
    session.remove(STORAGE_KEY$2);
    document.dispatchEvent(new CustomEvent(SCOPE_CHANGE_EVENT$2, { detail: { scope: "default" } }));
  }
  handleScopeChange(event) {
    const { scope } = event.detail;
    this.activeMenu = scope;
    session.set(STORAGE_KEY$2, scope);
    this.syncUi();
  }
  syncUi() {
    this.ensureMenuExistsOrFallback();
    this.syncButtons();
    this.syncMenus();
    this.syncBorder();
    this.syncSearchSelect();
  }
  // Mirror the active menu button into #header-search-select by matching the button's
  // trimmed text content against option text (case-insensitive). No hardcoded mapping —
  // adding a new menu button with a label that matches a select option just works.
  // When the active scope is 'default', reset to "Alle" (value="").
  syncSearchSelect() {
    const select = document.getElementById("header-search-select");
    if (!select) return;
    if (this.activeMenu === "default") {
      if (select.value === "") return;
      select.value = "";
      select.dispatchEvent(new Event("change"));
      return;
    }
    const root = this.getHeaderRoot();
    const activeButton = root.querySelector(`[data-menu-button="${this.activeMenu}"]`);
    const label = activeButton?.textContent?.trim().toLowerCase();
    if (!label) return;
    const match = Array.from(select.options).find((opt) => opt.text.trim().toLowerCase() === label);
    if (!match || select.value === match.value) return;
    select.value = match.value;
    select.dispatchEvent(new Event("change"));
  }
  syncBorder() {
    const root = this.getHeaderRoot();
    if (!(root instanceof HTMLElement)) return;
    if (this.activeMenu === "default") {
      const defaultColor = getComputedStyle(root).getPropertyValue("--scope-color-default").trim();
      if (defaultColor) {
        root.style.setProperty("--color-header-line", defaultColor);
      } else {
        root.style.removeProperty("--color-header-line");
      }
      root.removeAttribute("data-menu-active");
      return;
    }
    const activeButton = root.querySelector(`[data-menu-button="${this.activeMenu}"]`);
    if (!activeButton) return;
    const color = getComputedStyle(activeButton).getPropertyValue("--btn-color").trim();
    if (color) {
      root.style.setProperty("--color-header-line", color);
      root.setAttribute("data-menu-active", this.activeMenu);
    }
  }
  ensureMenuExistsOrFallback() {
    if (this.activeMenu === "default") return;
    const root = this.getHeaderRoot();
    const desktopItems = root.querySelectorAll(`full-menu .thb-full-menu:not(.thb-secondary-menu) > li[data-menu-scope="${this.activeMenu}"]`);
    const mobileItems = root.querySelectorAll(`#mobile-menu ul.mobile-menu > li[data-menu-scope="${this.activeMenu}"]`);
    if (desktopItems.length === 0 && mobileItems.length === 0) {
      this.activeMenu = "default";
    }
  }
  syncButtons() {
    const root = this.getHeaderRoot();
    const buttons = Array.from(root.querySelectorAll("[data-menu-button]"));
    buttons.forEach((button) => {
      const buttonMenu = button.dataset.menuButton;
      const isActive = buttonMenu != null && this.activeMenu === buttonMenu;
      button.toggleAttribute("data-active", isActive);
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", String(isActive));
    });
  }
  syncMenus() {
    const root = this.getHeaderRoot();
    const uls = Array.from(root.querySelectorAll("full-menu .thb-full-menu:not(.thb-secondary-menu)"));
    uls.forEach((ul) => {
      const scopedItems = Array.from(ul.querySelectorAll("li[data-menu-scope]"));
      scopedItems.forEach((item) => {
        const scope = item.dataset.menuScope;
        const shouldShow = scope === this.activeMenu;
        item.toggleAttribute("hidden", !shouldShow);
      });
    });
    const mobileUls = Array.from(root.querySelectorAll("#mobile-menu ul.mobile-menu"));
    mobileUls.forEach((ul) => {
      const scopedItems = Array.from(ul.querySelectorAll("li[data-menu-scope]"));
      scopedItems.forEach((item) => {
        const scope = item.dataset.menuScope;
        const shouldShow = scope === this.activeMenu;
        item.toggleAttribute("hidden", !shouldShow);
        item.classList.remove("first-visible-scoped");
      });
      const firstVisible = scopedItems.find((item) => !item.hasAttribute("hidden"));
      firstVisible?.classList.add("first-visible-scoped");
    });
  }
  getHeaderRoot() {
    return this.closest("theme-header") ?? document;
  }
}
class SiblingsElement extends HTMLElement {
  trigger = null;
  list = null;
  boundOnTriggerClick = this.onTriggerClick.bind(this);
  boundOnDocumentClick = this.onDocumentClick.bind(this);
  boundOnViewportChange = this.onViewportChange.bind(this);
  connectedCallback() {
    this.trigger = this.querySelector(".siblings-dropdown__trigger");
    this.list = this.querySelector(".siblings-dropdown__list");
    if (!this.trigger || !this.list) return;
    this.trigger.addEventListener("click", this.boundOnTriggerClick);
    document.addEventListener("click", this.boundOnDocumentClick);
    window.addEventListener("resize", this.boundOnViewportChange);
    window.addEventListener("scroll", this.boundOnViewportChange, true);
  }
  disconnectedCallback() {
    this.trigger?.removeEventListener("click", this.boundOnTriggerClick);
    document.removeEventListener("click", this.boundOnDocumentClick);
    window.removeEventListener("resize", this.boundOnViewportChange);
    window.removeEventListener("scroll", this.boundOnViewportChange, true);
  }
  onTriggerClick() {
    if (!this.trigger || !this.list) return;
    const isOpen = this.trigger.getAttribute("aria-expanded") === "true";
    if (!isOpen) {
      this.positionList();
    }
    this.trigger.setAttribute("aria-expanded", String(!isOpen));
    this.list.hidden = isOpen;
  }
  onDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.close();
    }
  }
  onViewportChange() {
    if (!this.list || this.list.hidden) return;
    this.positionList();
  }
  positionList() {
    if (!this.trigger || !this.list) return;
    const rect = this.trigger.getBoundingClientRect();
    this.list.style.top = `${rect.bottom + 4}px`;
    this.list.style.left = `${rect.left}px`;
    this.list.style.width = `${rect.width}px`;
  }
  close() {
    if (!this.trigger || !this.list) return;
    this.trigger.setAttribute("aria-expanded", "false");
    this.list.hidden = true;
  }
}
class ProductUpsellElement extends HTMLElement {
  form = null;
  boundOnSubmit = this.onSubmit.bind(this);
  connectedCallback() {
    this.form = this.closest("form");
    if (!this.form) return;
    this.form.addEventListener("submit", this.boundOnSubmit);
  }
  disconnectedCallback() {
    this.form?.removeEventListener("submit", this.boundOnSubmit);
  }
  onSubmit() {
    const checked = this.querySelectorAll(".product-upsell__checkbox:checked");
    if (checked.length === 0) return;
    const items = Array.from(checked).map((checkbox) => parseInt(checkbox.dataset.variantId || "", 10)).filter((id) => !Number.isNaN(id)).map((id) => ({ id, quantity: 1 }));
    if (items.length === 0) return;
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items })
    }).then(() => {
      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    });
  }
}
class VolumeDiscounts {
  static CART_EVENTS = ["cart:item-added", "line-item:change:end", "quick-add-cart:quantity-change"];
  static PROPS = {
    DISCOUNT: "_VolumeDiscount",
    COLLECTION_ID: "_VolumeDiscountCollectionID",
    COLLECTION_TITLE: "_VolumeDiscountCollectionTitle"
  };
  sectionId = null;
  init() {
    const sectionEl = document.querySelector('[id^="volume-discounts-"]');
    if (!sectionEl) {
      logger.warn("[VolumeDiscounts] No volume discounts section found on page");
      return;
    }
    this.sectionId = sectionEl.id.replace("volume-discounts-", "");
    VolumeDiscounts.CART_EVENTS.forEach((event) => {
      document.addEventListener(event, this.onCartChange);
    });
    void this.checkDiscounts();
  }
  onCartChange = async () => {
    await this.checkDiscounts();
  };
  async checkDiscounts() {
    try {
      const [sectionResponse, cartResponse] = await Promise.all([fetch(`/?section_id=${this.sectionId}`), fetch("/cart.js")]);
      if (!sectionResponse.ok) {
        throw new Error(`Section rendering API error: ${sectionResponse.status}`);
      }
      const html = await sectionResponse.text();
      const cart = await cartResponse.json();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const scriptEl = doc.querySelector("script");
      if (!scriptEl) {
        logger.warn("[VolumeDiscounts] No script element found in rendered section");
        return;
      }
      const fn = new Function(`${scriptEl.textContent ?? ""}; return window.houseTech && window.houseTech.volumeDiscounts;`);
      const discounts = fn() ?? [];
      const itemsToUpdate = discounts.filter((entry) => {
        const cartItem = cart.items.find((i) => String(i.variant_id) === entry.variantId);
        if (!cartItem) {
          return false;
        }
        const currentDiscount = cartItem.properties?.[VolumeDiscounts.PROPS.DISCOUNT] ?? "";
        const desiredDiscount = entry.eligible ? `${entry.discount}%` : "";
        return currentDiscount !== desiredDiscount;
      });
      if (itemsToUpdate.length > 0) {
        await this.applyDiscountProperties(itemsToUpdate);
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      }
    } catch (error) {
      logger.error("[VolumeDiscounts] Failed to check discounts", error);
    }
  }
  async applyDiscountProperties(discounts) {
    await Promise.all(
      discounts.map((entry) => {
        const properties = entry.eligible ? {
          [VolumeDiscounts.PROPS.DISCOUNT]: `${entry.discount}%`,
          [VolumeDiscounts.PROPS.COLLECTION_ID]: String(entry.collectionId),
          [VolumeDiscounts.PROPS.COLLECTION_TITLE]: entry.collectionTitle
        } : {
          [VolumeDiscounts.PROPS.DISCOUNT]: "",
          [VolumeDiscounts.PROPS.COLLECTION_ID]: "",
          [VolumeDiscounts.PROPS.COLLECTION_TITLE]: ""
        };
        return fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: entry.variantId,
            quantity: entry.qty,
            properties
          })
        });
      })
    );
    const eligible = discounts.filter((d) => d.eligible).length;
    discounts.length - eligible;
  }
  destroy() {
    VolumeDiscounts.CART_EVENTS.forEach((event) => {
      document.removeEventListener(event, this.onCartChange);
    });
  }
}
class ShowSubcollection extends HTMLElement {
  sectionId = "";
  showBtn = null;
  hideBtn = null;
  connectedCallback() {
    this.sectionId = this.dataset.sectionId ?? "";
    this.showBtn = this.querySelector(".subcollections-show-all");
    this.hideBtn = this.querySelector(".subcollections-hide-all");
    this.showBtn?.addEventListener("click", () => this.showAll());
    this.hideBtn?.addEventListener("click", () => this.hideAll());
    this.querySelectorAll("[data-parent-handle]").forEach((link) => {
      link.addEventListener("click", () => this.storeParent(link));
    });
  }
  storeParent(link) {
    const handle = link.dataset.parentHandle;
    const title = link.dataset.parentTitle;
    const url = link.dataset.parentUrl;
    if (!handle || !title || !url) return;
    const dest = new URL(link.href).pathname.replace(/^\/collections\//, "").split("/")[0];
    sessionStorage.setItem(`breadcrumb_parent_${dest}`, JSON.stringify({ handle, title, url }));
  }
  showAll() {
    this.querySelectorAll("[data-subcollection-extra]").forEach((el) => {
      el.classList.remove(`subcollection-extra-${this.sectionId}`);
    });
    if (this.showBtn) this.showBtn.style.display = "none";
    if (this.hideBtn) this.hideBtn.style.display = "inline-flex";
  }
  hideAll() {
    this.querySelectorAll("[data-subcollection-extra]").forEach((el) => {
      el.classList.add(`subcollection-extra-${this.sectionId}`);
    });
    if (this.hideBtn) this.hideBtn.style.display = "none";
    if (this.showBtn) this.showBtn.style.display = "inline-flex";
  }
}
class SubcollectionBreadcrumb extends HTMLElement {
  connectedCallback() {
    const handle = this.dataset.collectionHandle;
    if (!handle) return;
    const stored = sessionStorage.getItem(`breadcrumb_parent_${handle}`);
    if (!stored) return;
    let parent;
    try {
      parent = JSON.parse(stored);
    } catch {
      return;
    }
    if (!parent || parent.handle === handle) return;
    const separatorTemplate = this.querySelector("template[data-separator]");
    const link = document.createElement("a");
    link.href = parent.url;
    link.textContent = parent.title;
    this.appendChild(link);
    if (separatorTemplate) {
      const sep = document.createElement("span");
      sep.appendChild(separatorTemplate.content.cloneNode(true));
      this.appendChild(sep);
    }
  }
}
const SCOPE_CHANGE_EVENT$1 = "header-menu-scope-change";
const STORAGE_KEY$1 = "header-active-menu";
class HeroCollectionList extends HTMLElement {
  activeScope = "default";
  onScopeChange;
  constructor() {
    super();
    this.onScopeChange = this.handleScopeChange.bind(this);
  }
  connectedCallback() {
    const stored = session.get(STORAGE_KEY$1);
    if (stored && stored !== "default") {
      this.activeScope = stored;
    }
    this.applyScope();
    document.addEventListener(SCOPE_CHANGE_EVENT$1, this.onScopeChange);
  }
  disconnectedCallback() {
    document.removeEventListener(SCOPE_CHANGE_EVENT$1, this.onScopeChange);
  }
  handleScopeChange(event) {
    const { scope } = event.detail;
    this.activeScope = scope ?? "default";
    this.applyScope();
  }
  applyScope() {
    this.querySelectorAll("[data-scope]").forEach((el) => {
      el.toggleAttribute("hidden", el.dataset.scope !== this.activeScope);
    });
  }
}
const SCOPE_CHANGE_EVENT = "header-menu-scope-change";
const STORAGE_KEY = "header-active-menu";
class HeroMediaGrid extends HTMLElement {
  activeScope = "default";
  onScopeChange;
  constructor() {
    super();
    this.onScopeChange = this.handleScopeChange.bind(this);
  }
  connectedCallback() {
    const stored = session.get(STORAGE_KEY);
    if (stored && stored !== "default") {
      this.activeScope = stored;
    }
    this.applyScope();
    document.addEventListener(SCOPE_CHANGE_EVENT, this.onScopeChange);
  }
  disconnectedCallback() {
    document.removeEventListener(SCOPE_CHANGE_EVENT, this.onScopeChange);
  }
  handleScopeChange(event) {
    const { scope } = event.detail;
    this.activeScope = scope ?? "default";
    this.applyScope();
  }
  applyScope() {
    this.querySelectorAll("[data-scope]").forEach((el) => {
      el.toggleAttribute("hidden", el.dataset.scope !== this.activeScope);
    });
  }
}
const BATCH_SIZE = 20;
class FacetsTagLoader extends HTMLElement {
  products = [];
  specNames = [];
  async connectedCallback() {
    this.updateActiveFacets();
    this.listenForUpdates();
    const handle = this.dataset.collectionHandle;
    const searchTerm = this.dataset.searchTerm;
    console.log("[FacetsTagLoader] connectedCallback — handle:", handle, "| searchTerm:", searchTerm);
    if (!handle && !searchTerm) {
      console.log("[FacetsTagLoader] no handle or searchTerm, bailing");
      return;
    }
    const initialPanels = this.getTagPanels();
    this.specNames = [...new Set(initialPanels.map((el) => el.dataset.specName).filter(Boolean))];
    console.log("[FacetsTagLoader] specNames:", this.specNames);
    if (!this.specNames.length) {
      console.log("[FacetsTagLoader] no specNames found, bailing");
      return;
    }
    if (handle) {
      await this.fetchProducts(handle);
    } else {
      await this.fetchSearchProducts(searchTerm);
    }
    console.log("[FacetsTagLoader] products loaded:", this.products.length);
    this.runUpdates();
  }
  // ─── Core update cycle ───────────────────────────────────────────────────────
  runUpdates() {
    const activeTagValues = this.getActiveTagValues();
    const counts = this.computeCounts(activeTagValues);
    this.updateTagPanels(this.getTagPanels(), counts, activeTagValues);
    this.updateActiveFacets();
  }
  listenForUpdates() {
    document.addEventListener("facets:updated", () => this.runUpdates());
    window.addEventListener("popstate", () => setTimeout(() => this.runUpdates(), 0));
  }
  // ─── Data ────────────────────────────────────────────────────────────────────
  getTagPanels() {
    return Array.from(document.querySelectorAll("[data-spec-name]"));
  }
  getActiveTagValues() {
    return new URLSearchParams(window.location.search).getAll("filter.p.tag");
  }
  async fetchProducts(handle) {
    let page = 1;
    while (true) {
      const batch = await Promise.all(
        Array.from(
          { length: BATCH_SIZE },
          (_, i) => fetch(`/collections/${handle}/products.json?limit=250&page=${page + i}&fields=tags`).then((r) => r.ok ? r.json() : null).catch(() => null)
        )
      );
      let hasMore = false;
      for (const result of batch) {
        if (!result?.products?.length) break;
        for (const product of result.products) {
          const tagList = Array.isArray(product.tags) ? product.tags : typeof product.tags === "string" ? product.tags.split(",").map((t) => t.trim()) : [];
          const relevant = tagList.filter((tag) => tag.indexOf(":") !== -1);
          if (relevant.length) this.products.push(relevant);
        }
        if (result.products.length < 250) break;
        hasMore = true;
      }
      if (!hasMore) break;
      page += BATCH_SIZE;
    }
  }
  async fetchSearchProducts(searchTerm) {
    let page = 1;
    while (true) {
      const batch = await Promise.all(
        Array.from({ length: BATCH_SIZE }, (_, i) => {
          const url = `/search?q=${encodeURIComponent(searchTerm)}&type=product&view=tags-json&page=${page + i}`;
          return fetch(url).then((r) => r.ok ? r.json() : null).catch(() => null);
        })
      );
      let hasMore = false;
      for (const result of batch) {
        if (!result?.products?.length) break;
        for (const product of result.products) {
          const tagList = Array.isArray(product.tags) ? product.tags : typeof product.tags === "string" ? product.tags.split(",").map((t) => t.trim()) : [];
          const relevant = tagList.filter((tag) => tag.indexOf(":") !== -1);
          if (relevant.length) this.products.push(relevant);
        }
        if (result.next) hasMore = true;
      }
      if (!hasMore) break;
      page += BATCH_SIZE;
    }
  }
  // ─── Count computation ───────────────────────────────────────────────────────
  computeCounts(activeTagValues) {
    const counts = {};
    for (const specName of this.specNames) {
      const countMap = /* @__PURE__ */ new Map();
      counts[specName] = countMap;
      for (const tags of this.products) {
        for (const tag of tags) {
          const colon = tag.indexOf(":");
          if (colon !== -1 && tag.slice(0, colon).toLowerCase().trim() === specName) {
            if (!countMap.has(tag)) countMap.set(tag, 0);
          }
        }
      }
      const siblingActives = activeTagValues.filter((v) => {
        const colon = v.indexOf(":");
        return colon !== -1 && v.slice(0, colon).toLowerCase().trim() !== specName;
      });
      const filtered = siblingActives.length ? this.products.filter((tags) => siblingActives.every((a) => tags.includes(a))) : this.products;
      for (const tags of filtered) {
        for (const tag of tags) {
          const colon = tag.indexOf(":");
          if (colon !== -1 && tag.slice(0, colon).toLowerCase().trim() === specName) {
            countMap.set(tag, (countMap.get(tag) ?? 0) + 1);
          }
        }
      }
    }
    return counts;
  }
  // ─── Tag panels ──────────────────────────────────────────────────────────────
  updateTagPanels(panels, counts, activeTagValues) {
    for (const groupEl of panels) {
      const specName = groupEl.dataset.specName;
      const ul = groupEl.querySelector("ul");
      const template = ul?.querySelector("template[data-item-template]");
      const countMap = specName ? counts[specName] : void 0;
      if (!specName || !ul || !template || !countMap) continue;
      ul.querySelectorAll("li").forEach((li) => li.remove());
      const panelScope = groupEl.closest(".facet-drawer") ? "drawer" : "bar";
      for (const [fullValue, count] of countMap) {
        const isActive = activeTagValues.includes(fullValue);
        if (count === 0 && !isActive) continue;
        ul.appendChild(this.buildItem(template, fullValue, fullValue.slice(fullValue.indexOf(":") + 1), count, isActive, panelScope));
      }
      this.sortList(ul);
    }
  }
  // ─── Active facets ────────────────────────────────────────────────────────────
  updateActiveFacets() {
    const container = document.querySelector("facet-remove.active-facets");
    if (!container) return;
    const template = container.querySelector("template[data-remove-template]");
    if (!template) return;
    container.querySelectorAll("[data-active-pill]").forEach((el) => el.remove());
    const params = new URLSearchParams(window.location.search);
    const clearAll = container.querySelector(".active-facets__button-remove");
    for (const tagValue of params.getAll("filter.p.tag")) {
      if (tagValue.toLowerCase().startsWith("instock:")) continue;
      const label = tagValue.slice(tagValue.indexOf(":") + 1);
      container.insertBefore(this.buildActivePill(template, this.buildRemoveUrl("filter.p.tag", tagValue), label), clearAll ?? null);
    }
    for (const val of params.getAll("filter.v.availability")) {
      const removeParams = new URLSearchParams(params.toString());
      removeParams.delete("filter.v.availability");
      const href = window.location.pathname + (removeParams.toString() ? "?" + removeParams : "");
      container.insertBefore(this.buildActivePill(template, href, val === "1" ? window.theme?.strings?.inStock ?? "In stock" : val), clearAll ?? null);
    }
    const priceGte = params.get("filter.v.price.gte");
    const priceLte = params.get("filter.v.price.lte");
    if (priceGte || priceLte) {
      const removeParams = new URLSearchParams(params.toString());
      removeParams.delete("filter.v.price.gte");
      removeParams.delete("filter.v.price.lte");
      const href = window.location.pathname + (removeParams.toString() ? "?" + removeParams : "");
      container.insertBefore(this.buildActivePill(template, href, `${priceGte ?? "0"} - ${priceLte ?? "∞"}`), clearAll ?? null);
    }
    const search = (params.get("search") ?? "").trim();
    if (search) {
      const removeParams = new URLSearchParams(params.toString());
      removeParams.delete("search");
      const href = window.location.pathname + (removeParams.toString() ? "?" + removeParams : "");
      container.insertBefore(this.buildActivePill(template, href, `"${search}"`), clearAll ?? null);
    }
  }
  buildActivePill(template, href, label) {
    const a = template.content.firstElementChild.cloneNode(true);
    a.href = href;
    a.dataset.activePill = "true";
    const labelEl = a.querySelector(".active-facets__label");
    if (labelEl) labelEl.textContent = label;
    return a;
  }
  buildRemoveUrl(param, value) {
    const params = new URLSearchParams(window.location.search);
    const all = params.getAll(param);
    params.delete(param);
    for (const v of all) {
      if (v !== value) params.append(param, v);
    }
    const qs = params.toString();
    return window.location.pathname + (qs ? "?" + qs : "");
  }
  // ─── Helpers ─────────────────────────────────────────────────────────────────
  buildItem(template, fullValue, displayValue, count, isActive, panelScope) {
    const safeId = `filter-injected-${panelScope}-${fullValue}`.replace(/[^a-zA-Z0-9-]/g, "-");
    const li = template.content.firstElementChild.cloneNode(true);
    const input = li.querySelector("input");
    input.value = fullValue;
    input.id = safeId;
    input.checked = isActive;
    input.disabled = !isActive && count === 0;
    const label = li.querySelector("label");
    if (label) {
      label.htmlFor = safeId;
      label.classList.toggle("facet-checkbox--disabled", !isActive && count === 0);
    }
    const textEl = li.querySelector(".thb-filter-text");
    if (textEl) textEl.textContent = displayValue;
    const countEl = li.querySelector(".count");
    if (countEl) countEl.textContent = String(count);
    return li;
  }
  sortList(ul) {
    const items = Array.from(ul.querySelectorAll(":scope > li"));
    items.sort((a, b) => {
      const aText = a.querySelector(".thb-filter-text")?.textContent?.trim() ?? "";
      const bText = b.querySelector(".thb-filter-text")?.textContent?.trim() ?? "";
      return aText.localeCompare(bText, void 0, { numeric: true, sensitivity: "base" });
    });
    items.forEach((item) => ul.appendChild(item));
  }
}
function debounce(fn, ms) {
  let timer;
  return ((...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  });
}
let prevParams = window.location.search.slice(1);
let isLoading = false;
const responseCache = /* @__PURE__ */ new Map();
function getSectionId() {
  return document.getElementById("product-grid")?.dataset.id ?? "";
}
async function fetchSection(url) {
  const cached = responseCache.get(url);
  if (cached) return cached;
  const html = await fetch(url).then((r) => r.text());
  responseCache.set(url, html);
  return html;
}
function renderProductGrid(parsed) {
  const existing = document.querySelector("#ProductGridContainer .sidebar-container--products") ?? document.getElementById("ProductGridContainer");
  const fresh = parsed.querySelector("#ProductGridContainer .sidebar-container--products") ?? parsed.getElementById("ProductGridContainer");
  if (!existing || !fresh) return;
  existing.innerHTML = fresh.innerHTML;
  existing.querySelectorAll(".lazyload").forEach((el) => window.lazySizes?.loader?.unveil(el));
  const scrollAnchor = document.getElementById("ProductGridContainer") ?? existing;
  const headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--header-height"), 10) || 0;
  const top = scrollAnchor.getBoundingClientRect().top + document.documentElement.scrollTop - headerH - 30;
  window.scrollTo({ top, behavior: "smooth" });
}
function renderProductCount(parsed) {
  const fresh = parsed.getElementById("ProductCount");
  document.querySelectorAll(".thb-filter-count").forEach((el) => {
    if (fresh) {
      el.innerHTML = fresh.innerHTML;
      if (fresh.dataset.count) el.dataset.count = fresh.dataset.count;
    }
    el.classList.remove("loading");
  });
}
function renderSidebarPanels(parsed) {
  parsed.querySelectorAll("#FacetFiltersForm collapsible-row, #FacetFiltersFormMobile collapsible-row").forEach((freshRow) => {
    const existing = document.querySelector(`collapsible-row[data-index="${freshRow.dataset.index}"]`);
    if (existing) existing.innerHTML = freshRow.innerHTML;
  });
}
function fitBarFilters() {
  const row = document.querySelector(".facets-bar__filters-row");
  if (!row) return;
  const groups = Array.from(row.querySelectorAll(":scope > details.facets-bar__tag-group"));
  if (!groups.length) return;
  groups.forEach((g) => g.style.display = "");
  row.style.overflow = "hidden";
  void row.offsetWidth;
  for (let i = groups.length - 1; i >= 0; i--) {
    if (row.scrollWidth <= row.clientWidth) break;
    groups[i].style.display = "none";
    void row.offsetWidth;
  }
  if (row.querySelector("details[open]")) row.style.overflow = "visible";
  else row.style.overflow = "";
}
function renderActiveFacets(parsed) {
  const fresh = parsed.querySelector(".active-facets");
  const existing = document.querySelector(".active-facets");
  if (fresh && existing) existing.innerHTML = fresh.innerHTML;
}
function renderBarForm(parsed) {
  const fresh = parsed.querySelector("#FacetFiltersFormBar");
  const existing = document.querySelector("#FacetFiltersFormBar");
  if (!fresh || !existing) return;
  fresh.querySelectorAll('.facets-bar__inline-filter input[type="checkbox"]').forEach((freshCb) => {
    const existingCb = existing.querySelector(`#${freshCb.id}`);
    if (existingCb) {
      existingCb.checked = freshCb.checked;
      existingCb.disabled = freshCb.disabled;
      if (freshCb.dataset.instockFalseValue) existingCb.dataset.instockFalseValue = freshCb.dataset.instockFalseValue;
    }
  });
  existing.querySelectorAll('input[type="hidden"]').forEach((el) => el.remove());
  fresh.querySelectorAll('input[type="hidden"]').forEach((el) => {
    existing.insertBefore(el.cloneNode(true), existing.firstChild);
  });
  fresh.querySelectorAll("details[data-spec-name]").forEach((freshDetails) => {
    const name = freshDetails.dataset.specName;
    if (!name) return;
    const existingDetails = existing.querySelector(`details[data-spec-name="${name}"]`);
    if (existingDetails) existingDetails.innerHTML = freshDetails.innerHTML;
  });
  const freshCount = parsed.querySelector(".facets--bar-product-count");
  const existingCount = document.querySelector(".facets--bar-product-count");
  if (freshCount && existingCount) existingCount.textContent = freshCount.textContent;
  fitBarFilters();
}
function renderMobileCount(parsed) {
  const fresh = parsed.querySelector(".mobile-filter-count");
  const existing = document.querySelector(".mobile-filter-count");
  if (fresh && existing) {
    existing.innerHTML = fresh.innerHTML;
    existing.classList.remove("loading");
  }
  const clearBtn = document.querySelector(".mobile-filters-clear");
  if (clearBtn) {
    clearBtn.hidden = !parsed.querySelector(".active-facets__button:not(.active-facets__button-remove)");
  }
}
function updateURLHash(searchParams) {
  history.pushState({ searchParams }, "", `${window.location.pathname}${searchParams ? "?" + searchParams : ""}`);
}
function getMaxPageFromPagination(parsed) {
  const links = parsed.querySelectorAll('.pagination a[href*="page="]');
  let max = 1;
  links.forEach((link) => {
    const m = link.getAttribute("href")?.match(/[?&]page=(\d+)/);
    if (m) max = Math.max(max, parseInt(m[1], 10));
  });
  return max;
}
async function renderStandard(sectionId, serverQs) {
  const url = `${window.location.pathname}?section_id=${sectionId}&${serverQs}`;
  const html = await fetchSection(url);
  const parsed = new DOMParser().parseFromString(html, "text/html");
  renderProductGrid(parsed);
  renderProductCount(parsed);
  renderSidebarPanels(parsed);
  renderActiveFacets(parsed);
  renderBarForm(parsed);
  renderMobileCount(parsed);
}
async function renderInSearchMode(sectionId, serverQs, search) {
  const buildUrl = (page) => `${window.location.pathname}?section_id=${sectionId}&${serverQs}${serverQs ? "&" : ""}page=${page}`;
  const page1Html = await fetchSection(buildUrl(1));
  const parsed1 = new DOMParser().parseFromString(page1Html, "text/html");
  const totalPages = getMaxPageFromPagination(parsed1);
  const additionalPages = [];
  if (totalPages > 1) {
    const htmls = await Promise.all(Array.from({ length: totalPages - 1 }, (_, i) => fetchSection(buildUrl(i + 2))));
    additionalPages.push(...htmls.map((h) => new DOMParser().parseFromString(h, "text/html")));
  }
  renderProductCount(parsed1);
  renderSidebarPanels(parsed1);
  renderActiveFacets(parsed1);
  renderBarForm(parsed1);
  renderMobileCount(parsed1);
  const needle = search.toLowerCase();
  const matching = [];
  for (const parsed of [parsed1, ...additionalPages]) {
    parsed.querySelectorAll("#product-grid > li").forEach((card) => {
      const title = card.querySelector(".product-card-title")?.textContent?.toLowerCase() ?? "";
      if (title.includes(needle)) matching.push(card);
    });
  }
  const existingProducts = document.querySelector("#ProductGridContainer .sidebar-container--products");
  const freshProducts = parsed1.querySelector("#ProductGridContainer .sidebar-container--products");
  if (existingProducts && freshProducts) {
    existingProducts.innerHTML = freshProducts.innerHTML;
    const grid = existingProducts.querySelector("#product-grid");
    if (grid) {
      grid.querySelectorAll(":scope > li:not(.loading-overlay)").forEach((li) => li.remove());
      const loadingOverlay = grid.querySelector(":scope > .loading-overlay");
      matching.forEach((card) => {
        const clone = card.cloneNode(true);
        if (loadingOverlay) grid.insertBefore(clone, loadingOverlay);
        else grid.appendChild(clone);
      });
    }
    existingProducts.querySelectorAll("pagination-theme.pagination").forEach((el) => el.remove());
    existingProducts.querySelectorAll(".lazyload").forEach((el) => window.lazySizes?.loader?.unveil(el));
  }
  const countEl = document.getElementById("ProductCount");
  if (countEl) {
    countEl.dataset.count = String(matching.length);
    const label = countEl.querySelector(".facets__label");
    if (label) label.textContent = `${matching.length} ${matching.length === 1 ? "resultat" : "resultater"}`;
  }
  const anchor = document.getElementById("ProductGridContainer");
  if (anchor) {
    const headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--header-height"), 10) || 0;
    const top = anchor.getBoundingClientRect().top + document.documentElement.scrollTop - headerH - 30;
    window.scrollTo({ top, behavior: "smooth" });
  }
}
function renderPage(searchParams, updateURL = true) {
  if (isLoading) return;
  const sectionId = getSectionId();
  if (!sectionId) return;
  isLoading = true;
  prevParams = searchParams;
  document.getElementById("ProductGridContainer")?.querySelector(".collection")?.classList.add("loading");
  document.querySelectorAll(".thb-filter-count, .mobile-filter-count").forEach((el) => el.classList.add("loading"));
  const urlParams = new URLSearchParams(searchParams);
  const search = (urlParams.get("search") ?? "").trim();
  urlParams.delete("search");
  const serverQs = urlParams.toString();
  const work = search ? renderInSearchMode(sectionId, serverQs, search) : renderStandard(sectionId, serverQs);
  work.then(() => {
    if (updateURL) updateURLHash(searchParams);
    document.dispatchEvent(new CustomEvent("facets:updated", { detail: { searchParams } }));
  }).finally(() => {
    isLoading = false;
  });
}
window.addEventListener("popstate", (e) => {
  const params = e.state?.searchParams ?? window.location.search.slice(1);
  if (params === prevParams) return;
  renderPage(params, false);
});
requestAnimationFrame(fitBarFilters);
const _barFitObserver = new ResizeObserver(fitBarFilters);
const _barFitForm = document.querySelector("#FacetFiltersFormBar");
if (_barFitForm) _barFitObserver.observe(_barFitForm);
const _barFiltersRow = document.querySelector(".facets-bar__filters-row");
if (_barFiltersRow) {
  _barFiltersRow.addEventListener(
    "toggle",
    () => {
      _barFiltersRow.style.overflow = _barFiltersRow.querySelector("details[open]") ? "visible" : "";
    },
    true
  );
}
function findRemappedRadio(target) {
  const li = target.closest("li");
  return li?.querySelector('input[type="radio"][data-real-name]') ?? null;
}
document.addEventListener("mousedown", (e) => {
  const radio = findRemappedRadio(e.target);
  if (radio?.checked) radio.dataset.willUncheck = "1";
});
document.addEventListener("click", (e) => {
  const t = e.target;
  if (!(t instanceof HTMLInputElement) || t.type !== "radio" || !t.dataset.realName) return;
  if (t.dataset.willUncheck !== "1") return;
  delete t.dataset.willUncheck;
  t.checked = false;
  t.dispatchEvent(new Event("input", { bubbles: true }));
  t.dispatchEvent(new Event("change", { bubbles: true }));
});
document.addEventListener("click", (e) => {
  const target = e.target;
  const inside = target.closest(".facets--bar .facets-bar__tag-group");
  if (!inside) {
    document.querySelectorAll(".facets--bar .facets-bar__tag-group[open]").forEach((d) => d.removeAttribute("open"));
    return;
  }
  const summary = target.closest(".facets--bar .facets-bar__tag-group > summary");
  if (!summary) return;
  const clicked = summary.closest("details");
  document.querySelectorAll(".facets--bar .facets-bar__tag-group[open]").forEach((d) => {
    if (d !== clicked) d.removeAttribute("open");
  });
});
class MoreFiltersElement extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", (e) => {
      e.preventDefault();
      const ul = this.closest(".thb-filter-content")?.querySelector("ul");
      if (!ul) return;
      const isOpen = ul.hasAttribute("filters-open");
      ul.toggleAttribute("filters-open", !isOpen);
      this.textContent = isOpen ? window.theme?.strings?.showMore ?? "Show more" : window.theme?.strings?.showLess ?? "Show less";
    });
  }
}
class DrawerToggleElement extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", () => {
      const drawer = document.getElementById("Facet-Drawer");
      if (!drawer) return;
      document.body.classList.toggle("open-cc");
      drawer.classList.toggle("active");
      drawer.removeAttribute("inert");
    });
  }
}
class FacetToolbarElement extends HTMLElement {
  connectedCallback() {
    const buttons = Array.from(this.querySelectorAll(".facets--bar-view-as-button"));
    const grid = document.querySelector("#ProductGridContainer");
    buttons.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        buttons.forEach((b) => b.removeAttribute("disabled"));
        btn.setAttribute("disabled", "");
        const val = btn.dataset.value ?? "grid";
        if (grid) grid.dataset.layout = val;
        localStorage.setItem("product-layout", val);
      });
    });
    if (localStorage.getItem("product-layout") === "list") {
      this.querySelector(".facets--bar-view-as-button-list")?.click();
    }
    this.querySelector("#compare_toggle")?.addEventListener("change", (e) => {
      e.preventDefault();
      document.body.classList.toggle("compare-true");
    });
  }
}
class FacetRemoveElement extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", (e) => {
      const link = e.target.closest("a");
      if (!link) return;
      e.preventDefault();
      const qs = link.href.includes("?") ? link.href.slice(link.href.indexOf("?") + 1) : "";
      renderPage(qs);
    });
  }
}
class FacetFiltersFormElement extends HTMLElement {
  form = null;
  ready = false;
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.form = this.querySelector("form");
    if (!this.form) return;
    if (this.closest(".facet-drawer")) {
      this.form.addEventListener("submit", (e) => {
        e.preventDefault();
        renderPage(this.buildParams());
        document.dispatchEvent(new CustomEvent("panel:close"));
      });
      this.addEventListener(
        "input",
        debounce((e) => {
          if (!e.target.matches('input[type="checkbox"], input[type="radio"]')) return;
          this.updateDrawerCount();
        }, 300)
      );
    } else {
      this.form.addEventListener(
        "input",
        debounce(() => renderPage(this.buildParams()), 400)
      );
      const sortSelect = this.querySelector(".facet-filters__sort");
      if (sortSelect) {
        sortSelect.addEventListener(
          "change",
          debounce(() => renderPage(this.buildParams()), 400)
        );
      }
    }
  }
  buildParams() {
    if (!this.form) return "";
    const params = new URLSearchParams();
    new FormData(this.form).forEach((value, key) => params.append(key, value));
    const sortSelect = this.querySelector(".facet-filters__sort");
    if (sortSelect?.value) params.set("sort_by", sortSelect.value);
    this.form.querySelectorAll('input[type="radio"][data-real-name]:checked').forEach((radio) => {
      params.delete(radio.name);
      params.append(radio.dataset.realName, radio.value);
    });
    if (params.get("filter.v.price.gte") === "0.00") params.delete("filter.v.price.gte");
    const sliderMax = document.querySelector(".price_slider")?.dataset.max;
    if (sliderMax && params.get("filter.v.price.lte") === parseFloat(sliderMax).toFixed(2)) {
      params.delete("filter.v.price.lte");
    }
    const instockCb = this.form.querySelector("#BarInstock");
    if (instockCb && !instockCb.checked) {
      const falseValue = instockCb.dataset.instockFalseValue;
      if (falseValue) {
        const hadInstockFilter = new URLSearchParams(window.location.search).getAll("filter.p.tag").some((v) => v.toLowerCase().startsWith("instock:"));
        if (hadInstockFilter) {
          const otherTags = params.getAll("filter.p.tag").filter((v) => !v.toLowerCase().startsWith("instock:"));
          params.delete("filter.p.tag");
          for (const v of otherTags) params.append("filter.p.tag", v);
          params.append("filter.p.tag", falseValue);
        }
      }
    }
    const liveSearch = (new URLSearchParams(window.location.search).get("search") ?? "").trim();
    if (liveSearch) params.set("search", liveSearch);
    return params.toString();
  }
  async updateDrawerCount() {
    const sectionId = getSectionId();
    if (!sectionId) return;
    const countEl = document.querySelector(".mobile-filter-count");
    countEl?.classList.add("loading");
    try {
      const url = `${window.location.pathname}?section_id=${sectionId}&${this.buildParams()}`;
      const html = await fetchSection(url);
      const parsed = new DOMParser().parseFromString(html, "text/html");
      const freshLabel = parsed.querySelector(".mobile-filter-count .facets__label");
      const currentLabel = countEl?.querySelector(".facets__label");
      if (freshLabel && currentLabel) currentLabel.innerHTML = freshLabel.innerHTML;
    } finally {
      countEl?.classList.remove("loading");
    }
  }
}
class PriceSliderElement extends HTMLElement {
  connectedCallback() {
    const slider = this.querySelector(".price_slider");
    const amounts = this.querySelector(".price_slider_amount");
    const formEl = this.closest("facet-filters-form") ?? document.querySelector("facet-filters-form");
    if (!slider || !amounts || !formEl) return;
    noUiSlider.create(slider, {
      start: [parseFloat(slider.dataset.minValue || "0"), parseFloat(slider.dataset.maxValue || slider.dataset.max || "0")],
      connect: true,
      step: 10,
      direction: document.dir || "ltr",
      handleAttributes: [{ "aria-label": "lower" }, { "aria-label": "upper" }],
      range: { min: 0, max: parseFloat(slider.dataset.max || "0") }
    });
    slider.noUiSlider.on("update", (values) => {
      amounts.querySelector(".field__input_min").value = values[0];
      amounts.querySelector(".field__input_max").value = values[1];
    });
    slider.noUiSlider.on("change", () => {
      formEl.querySelector("form")?.dispatchEvent(new Event("input"));
    });
  }
}
class FilterProductSearchElement extends HTMLElement {
  input;
  connectedCallback() {
    this.input = this.querySelector(".filter-product-search__input");
    if (!this.input) return;
    const legacyResults = this.querySelector(".filter-product-search__results");
    if (legacyResults) legacyResults.hidden = true;
    const initial = new URLSearchParams(window.location.search).get("search") ?? "";
    this.input.value = initial;
    if (initial.trim()) queueMicrotask(() => this.apply());
    this.input.addEventListener(
      "input",
      debounce(() => this.apply(), 300)
    );
    document.addEventListener("facets:updated", () => {
      const current = new URLSearchParams(window.location.search).get("search") ?? "";
      if (this.input.value !== current) this.input.value = current;
    });
  }
  apply() {
    const term = this.input.value.trim();
    const params = new URLSearchParams(window.location.search);
    if (term) params.set("search", term);
    else params.delete("search");
    renderPage(params.toString());
  }
}
class ProductBundle extends HTMLElement {
  activeModal = null;
  carouselViewport = null;
  carouselTrack = null;
  previousButton = null;
  nextButton = null;
  qty = 1;
  keyHandler = this.onKeyDown.bind(this);
  carouselScrollHandler = this.updateCarouselNavigation.bind(this);
  resizeHandler = this.updateCarouselNavigation.bind(this);
  previousClickHandler = () => this.scrollCarousel(-1);
  nextClickHandler = () => this.scrollCarousel(1);
  connectedCallback() {
    this.setupCarousel();
    this.setupQtySelector();
    this.setupAddToCart();
    this.querySelectorAll("[data-bundle-switch]").forEach((btn) => {
      btn.addEventListener("click", () => this.openModal(btn.dataset.bundleSwitch));
    });
    this.querySelectorAll("[data-bundle-close]").forEach((el) => {
      el.addEventListener("click", () => this.closeModal());
    });
    this.querySelectorAll("product-card[data-bundle-toggleable]").forEach((card) => {
      card.addEventListener("click", (e) => {
        const target = e.target;
        if (target.closest("[data-bundle-switch]") || target.closest(".bundle-card__variant-picker")) return;
        if (target.closest(".product-card-title") && !card.classList.contains("is-excluded")) return;
        e.preventDefault();
        card.classList.toggle("is-excluded");
        this.updatePriceSummary();
      });
    });
    this.querySelectorAll("[data-bundle-card-variant-select]").forEach((select) => {
      select.addEventListener("change", () => this.handleVariantChange(select));
    });
    this.querySelectorAll("[data-bundle-option]").forEach((option) => {
      option.addEventListener("click", (e) => {
        const target = e.target;
        if (target.closest("a, button, select, option, input, textarea, label, [data-bundle-option-interactive]")) {
          return;
        }
        e.preventDefault();
        this.selectProduct(option.dataset.bundleSlot, option.dataset.cardIndex);
        this.closeModal();
      });
    });
    this.querySelectorAll("[data-bundle-select]").forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.selectProduct(button.dataset.bundleSlot, button.dataset.cardIndex);
        this.closeModal();
      });
    });
    this.querySelectorAll("[data-bundle-option-interactive]").forEach((element) => {
      ["click", "mousedown", "touchstart", "keydown"].forEach((eventName) => {
        element.addEventListener(eventName, (e) => e.stopPropagation());
      });
    });
    this.updatePriceSummary();
  }
  disconnectedCallback() {
    this.teardownCarousel();
    document.removeEventListener("keydown", this.keyHandler);
  }
  // ─── Price summary ─────────────────────────────────────────────
  formatMoney(cents) {
    const amount = cents / 100;
    if (Number.isInteger(amount)) return `${amount.toLocaleString("da-DK")} kr`;
    return `${amount.toLocaleString("da-DK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`;
  }
  getActiveItems() {
    const items = [];
    this.querySelectorAll("[data-bundle-slot]").forEach((slot) => {
      const visibleCard = [...slot.querySelectorAll("[data-card-index]")].find((card) => !card.hidden);
      if (!visibleCard) return;
      const productCard = visibleCard.querySelector("product-card");
      if (!productCard || productCard.classList.contains("is-excluded")) return;
      const variantId = productCard.dataset.bundleVariantId ?? "";
      const price = parseInt(productCard.dataset.bundlePrice ?? "0", 10);
      const comparePrice = parseInt(productCard.dataset.bundleComparePrice ?? "0", 10);
      const costPriceCents = parseInt(productCard.dataset.bundleCostPriceCents ?? "0", 10);
      const itemQty = Math.max(1, parseInt(productCard.dataset.bundleItemQty ?? "1", 10));
      const metaobjectId = slot.dataset.bundleMetaobjectId ?? "";
      if (variantId) items.push({ variantId, price, comparePrice, costPriceCents, itemQty, metaobjectId });
    });
    return items;
  }
  getBundleDiscountConfig() {
    const raw = this.dataset.bundleDiscountTiers;
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  getPriceMatchedBundleDiscount(totalCents) {
    const discountConfig = this.getBundleDiscountConfig();
    if (!discountConfig?.tiers?.length) return null;
    try {
      const totalKr = totalCents / 100;
      return discountConfig.tiers.find((tier) => totalKr >= tier.priceFrom && (!tier.priceTo || totalKr <= tier.priceTo)) ?? null;
    } catch {
      return null;
    }
  }
  getBundleDiscount(items, totalCents) {
    const matchedTier = this.getPriceMatchedBundleDiscount(totalCents);
    if (!matchedTier) return null;
    const minProfitMargin = matchedTier.minProfitMargin ?? this.getBundleDiscountConfig()?.minProfitMargin;
    if (minProfitMargin == null) return matchedTier;
    const bundleMarginPercent = this.getBundleMarginPercent(items, matchedTier.discountPercent);
    if (bundleMarginPercent === null || bundleMarginPercent < minProfitMargin) return null;
    return matchedTier;
  }
  isFullBundle(items) {
    const slotCount = parseInt(this.dataset.bundleSlotCount ?? "0", 10);
    return slotCount > 0 && items.length === slotCount;
  }
  getBundleMarginPercent(items, discountPercent) {
    const bundleSubtotal = items.reduce((sum, item) => sum + item.price * item.itemQty, 0) * this.qty;
    const discountedTotal = Math.round(bundleSubtotal * (1 - discountPercent / 100));
    if (discountedTotal <= 0) return null;
    const totalCost = items.reduce((sum, item) => sum + item.costPriceCents * item.itemQty, 0) * this.qty;
    const marginPercent = (discountedTotal - totalCost) * 100 / discountedTotal;
    return Math.round(marginPercent * 10) / 10;
  }
  getSharedBundleProperties(items) {
    const bundleSubtotal = items.reduce((sum, item) => sum + item.price * item.itemQty, 0) * this.qty;
    const matchedTier = this.getPriceMatchedBundleDiscount(bundleSubtotal);
    const discountConfig = this.getBundleDiscountConfig();
    const bundleMarginPercent = matchedTier ? this.getBundleMarginPercent(items, matchedTier.discountPercent) : null;
    const tierValue = matchedTier ? matchedTier.tier ?? matchedTier.priceFrom : "";
    const minProfitMargin = matchedTier?.minProfitMargin ?? discountConfig?.minProfitMargin;
    return {
      _BundleMarginPercent: bundleMarginPercent === null ? "" : String(bundleMarginPercent),
      _BundleMinMarginPercent: minProfitMargin == null ? "" : String(minProfitMargin),
      _BundleTier: tierValue === "" ? "" : String(tierValue),
      _BundleDiscount: matchedTier ? `${matchedTier.discountPercent}%` : ""
    };
  }
  updatePriceSummary() {
    const items = this.getActiveItems();
    const total = items.reduce((sum, item) => sum + item.price * item.itemQty, 0) * this.qty;
    const activeTier = this.getBundleDiscount(items, total);
    const discountPct = activeTier?.discountPercent ?? 0;
    const totalEl = this.querySelector("[data-bundle-total-amount]");
    const compareRowEl = this.querySelector("[data-bundle-compare-row]");
    const compareAmountEl = this.querySelector("[data-bundle-compare-amount]");
    const savingsEl = this.querySelector("[data-bundle-savings]");
    const savingsAmountEl = this.querySelector("[data-bundle-savings-amount]");
    const tierInfoEl = this.querySelector("[data-bundle-tier-info]");
    const tierPctEl = this.querySelector("[data-bundle-tier-pct]");
    if (tierInfoEl && tierPctEl) {
      if (discountPct > 0) {
        tierPctEl.textContent = String(discountPct);
        tierInfoEl.hidden = false;
      } else {
        tierInfoEl.hidden = true;
      }
    }
    if (discountPct > 0) {
      const discountedTotal = Math.round(total * (1 - discountPct / 100));
      const savings = total - discountedTotal;
      if (totalEl) totalEl.textContent = this.formatMoney(discountedTotal);
      if (compareRowEl && compareAmountEl) {
        compareAmountEl.textContent = this.formatMoney(total);
        compareRowEl.hidden = false;
      }
      if (savingsEl && savingsAmountEl) {
        savingsAmountEl.textContent = this.formatMoney(savings);
        savingsEl.hidden = false;
      }
    } else {
      const savings = items.reduce((sum, item) => item.comparePrice > item.price ? sum + (item.comparePrice - item.price) * item.itemQty : sum, 0) * this.qty;
      if (totalEl) totalEl.textContent = this.formatMoney(total);
      if (compareRowEl) compareRowEl.hidden = true;
      if (savingsEl && savingsAmountEl) {
        if (savings > 0) {
          savingsAmountEl.textContent = this.formatMoney(savings);
          savingsEl.hidden = false;
        } else {
          savingsEl.hidden = true;
        }
      }
    }
  }
  handleVariantChange(select) {
    const card = select.closest("product-card");
    if (!card) return;
    const selectedOption = select.options[select.selectedIndex];
    const price = parseInt(selectedOption.dataset.price ?? "0", 10);
    const comparePrice = parseInt(selectedOption.dataset.comparePrice ?? "0", 10);
    card.dataset.bundlePrice = String(price);
    card.dataset.bundleComparePrice = String(comparePrice);
    card.dataset.bundleVariantId = select.value;
    const priceAmountEl = card.querySelector(".price ins .amount");
    if (priceAmountEl) priceAmountEl.textContent = this.formatMoney(price);
    const priceEl = card.querySelector(".price");
    if (priceEl) {
      let delEl = priceEl.querySelector("del");
      if (comparePrice > price) {
        if (!delEl) {
          delEl = document.createElement("del");
          const span = document.createElement("span");
          span.className = "amount discounted";
          delEl.appendChild(span);
          priceEl.appendChild(delEl);
        }
        const delAmount = delEl.querySelector(".amount");
        if (delAmount) delAmount.textContent = this.formatMoney(comparePrice);
      } else if (delEl) {
        delEl.remove();
      }
    }
    this.updatePriceSummary();
  }
  // ─── Qty selector ──────────────────────────────────────────────
  setupQtySelector() {
    const downBtn = this.querySelector("[data-bundle-qty-down]");
    const upBtn = this.querySelector("[data-bundle-qty-up]");
    const countEl = this.querySelector("[data-bundle-qty-count]");
    if (!downBtn || !upBtn || !countEl) return;
    downBtn.addEventListener("click", () => {
      if (this.qty <= 1) return;
      this.qty--;
      countEl.textContent = String(this.qty);
      downBtn.disabled = this.qty <= 1;
      this.updatePriceSummary();
    });
    upBtn.addEventListener("click", () => {
      this.qty++;
      countEl.textContent = String(this.qty);
      downBtn.disabled = false;
      this.updatePriceSummary();
    });
  }
  // ─── Add to cart ───────────────────────────────────────────────
  setupAddToCart() {
    const button = this.querySelector("[data-bundle-add-to-cart]");
    if (!button) return;
    button.addEventListener("click", async () => {
      const items = this.getActiveItems();
      if (!items.length) return;
      const originalText = button.textContent ?? "Tilføj til kurv";
      button.disabled = true;
      button.textContent = "Tilføjer...";
      try {
        const isFullBundle = this.isFullBundle(items);
        const sharedBundleProperties = isFullBundle ? this.getSharedBundleProperties(items) : null;
        const parentId = parseInt(items[0].variantId, 10);
        const payload = {
          items: items.map(({ variantId, itemQty }, index) => {
            const id = parseInt(variantId, 10);
            const isParent = index === 0;
            return {
              id,
              quantity: this.qty * itemQty,
              ...isParent && sharedBundleProperties ? { properties: sharedBundleProperties } : {},
              ...!isParent && isFullBundle ? { parent_id: parentId } : {}
            };
          })
        };
        console.group("[ProductBundle] add-to-cart debug");
        console.log("isFullBundle:", isFullBundle, `(${items.length}/${parseInt(this.dataset.bundleSlotCount ?? "0", 10)} slots)`);
        console.log("parent variantId:", parentId);
        console.log("sharedBundleProperties:", sharedBundleProperties);
        console.log("payload:", JSON.stringify(payload, null, 2));
        console.groupEnd();
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
        const responseBody = await response.clone().json().catch(() => null);
        console.log("[ProductBundle] response status:", response.status, responseBody);
        if (!response.ok) throw new Error("Add to cart failed");
        button.disabled = false;
        button.textContent = originalText;
        const cartDrawer = document.getElementById("Cart-Drawer");
        if (cartDrawer?.open) {
          cartDrawer.open();
        } else {
          document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
        }
      } catch {
        button.disabled = false;
        button.textContent = originalText;
      }
    });
  }
  // ─── Carousel ──────────────────────────────────────────────────
  setupCarousel() {
    this.carouselViewport = this.querySelector("[data-bundle-viewport]");
    this.carouselTrack = this.querySelector("[data-bundle-track]");
    this.previousButton = this.querySelector("[data-bundle-prev]");
    this.nextButton = this.querySelector("[data-bundle-next]");
    if (!this.carouselViewport || !this.carouselTrack || !this.previousButton || !this.nextButton) return;
    this.carouselViewport.addEventListener("scroll", this.carouselScrollHandler, { passive: true });
    this.previousButton.addEventListener("click", this.previousClickHandler);
    this.nextButton.addEventListener("click", this.nextClickHandler);
    window.addEventListener("resize", this.resizeHandler);
    window.requestAnimationFrame(() => this.updateCarouselNavigation());
  }
  teardownCarousel() {
    this.carouselViewport?.removeEventListener("scroll", this.carouselScrollHandler);
    this.previousButton?.removeEventListener("click", this.previousClickHandler);
    this.nextButton?.removeEventListener("click", this.nextClickHandler);
    window.removeEventListener("resize", this.resizeHandler);
    this.carouselViewport = null;
    this.carouselTrack = null;
    this.previousButton = null;
    this.nextButton = null;
  }
  getCarouselStep() {
    if (!this.carouselTrack) return 0;
    const slots = this.carouselTrack.querySelectorAll(".bundle-slot");
    const [firstSlot, secondSlot] = slots;
    if (firstSlot && secondSlot) {
      return secondSlot.offsetLeft - firstSlot.offsetLeft;
    }
    if (firstSlot) {
      return firstSlot.getBoundingClientRect().width;
    }
    return 0;
  }
  scrollCarousel(direction) {
    if (!this.carouselViewport) return;
    const scrollStep = this.getCarouselStep();
    if (!scrollStep) return;
    this.carouselViewport.scrollBy({
      left: scrollStep * direction,
      behavior: "smooth"
    });
  }
  updateCarouselNavigation() {
    if (!this.carouselViewport || !this.previousButton || !this.nextButton) return;
    const maxScrollLeft = Math.max(this.carouselViewport.scrollWidth - this.carouselViewport.clientWidth, 0);
    const hasOverflow = maxScrollLeft > 4;
    const isAtStart = this.carouselViewport.scrollLeft <= 4;
    const isAtEnd = this.carouselViewport.scrollLeft >= maxScrollLeft - 4;
    this.previousButton.hidden = !hasOverflow || isAtStart;
    this.nextButton.hidden = !hasOverflow || isAtEnd;
    this.previousButton.disabled = !hasOverflow || isAtStart;
    this.nextButton.disabled = !hasOverflow || isAtEnd;
  }
  // ─── Modal ─────────────────────────────────────────────────────
  openModal(slotIndex) {
    this.closeModal();
    const modal = this.querySelector(`[data-bundle-modal="${slotIndex}"]`);
    if (!modal) return;
    this.activeModal = modal;
    modal.removeAttribute("hidden");
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", this.keyHandler);
    window.requestAnimationFrame(() => {
      const focusTarget = modal.querySelector("[data-bundle-select].is-selected, [data-bundle-select]:not([disabled]), [data-bundle-close]");
      focusTarget?.focus();
    });
  }
  closeModal() {
    if (!this.activeModal) return;
    this.activeModal.setAttribute("hidden", "");
    document.body.style.overflow = "";
    document.removeEventListener("keydown", this.keyHandler);
    this.activeModal = null;
  }
  selectProduct(slotIndex, cardIndex) {
    const slot = this.querySelector(`[data-bundle-slot="${slotIndex}"]`);
    if (!slot) return;
    slot.querySelectorAll("[data-card-index]").forEach((card) => {
      card.hidden = card.dataset.cardIndex !== cardIndex;
    });
    const newProductCard = slot.querySelector(`[data-card-index="${cardIndex}"] product-card`);
    if (newProductCard) newProductCard.classList.remove("is-excluded");
    const modal = this.querySelector(`[data-bundle-modal="${slotIndex}"]`);
    if (modal) {
      modal.querySelectorAll("[data-bundle-option]").forEach((opt) => {
        const isSelected = opt.dataset.cardIndex === cardIndex;
        opt.classList.toggle("is-selected", isSelected);
        const button = opt.querySelector("[data-bundle-select]");
        if (!button) return;
        button.classList.toggle("is-selected", isSelected);
        button.disabled = isSelected;
        button.setAttribute("aria-pressed", String(isSelected));
      });
    }
    this.updatePriceSummary();
  }
  onKeyDown(e) {
    if (e.key === "Escape") this.closeModal();
  }
}
class ProductTabbedContent extends HTMLElement {
  sectionIds = ["product-description", "product-specifications", "product-documents"];
  boundOnScroll = this.onScroll.bind(this);
  connectedCallback() {
    this.querySelectorAll(".tabbed-content__tab[data-tab]").forEach((tab) => {
      tab.addEventListener("click", this.onTabClick.bind(this));
    });
    window.addEventListener("scroll", this.boundOnScroll, { passive: true });
    this.onScroll();
  }
  disconnectedCallback() {
    window.removeEventListener("scroll", this.boundOnScroll);
  }
  getStickyOffset() {
    const stickyBar = this.querySelector(":scope > div");
    const stickyBarHeight = stickyBar ? stickyBar.getBoundingClientRect().height : 0;
    const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--header-height") || "0", 10);
    return headerHeight + stickyBarHeight + 30;
  }
  onScroll() {
    const offset = this.getStickyOffset() + 1;
    let activeId = this.sectionIds[0];
    for (const id of this.sectionIds) {
      const el = document.getElementById(id);
      if (el && el.getBoundingClientRect().top <= offset) {
        activeId = id;
      }
    }
    this.setActive(activeId.replace("product-", ""));
  }
  onTabClick(e) {
    const tab = e.currentTarget;
    const href = tab.getAttribute("href");
    if (!href) return;
    const target = document.querySelector(href);
    if (!target) return;
    e.preventDefault();
    const offset = this.getStickyOffset();
    const top = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });
  }
  setActive(tabName) {
    const tabs = Array.from(this.querySelectorAll(".tabbed-content__tab[data-tab]"));
    const activeIndex = tabs.findIndex((tab) => tab.dataset.tab === tabName);
    tabs.forEach((tab, i) => {
      tab.classList.toggle("is-active", i <= activeIndex);
    });
  }
}
const dataLayer = new DataLayer();
dataLayer.init();
const scripts = new ThemeScripts();
scripts.load({
  id: "gtm",
  strategy: "on-default-consent-set",
  gtmConfig: {
    containerId: "GTM-MCNN27W9",
    scriptName: "2ES0Urclbemmj.js",
    serverUrl: "https://cdn.house-tech.dk"
  }
});
if (!customElements.get("cookie-banner")) {
  customElements.define("cookie-banner", CookieBanner);
}
customElements.define("geolocator-component", Geolocator);
if (!customElements.get("header-menu-buttons")) {
  customElements.define("header-menu-buttons", HeaderMenuButtons);
}
if (!customElements.get("product-tabbed-content")) {
  customElements.define("product-tabbed-content", ProductTabbedContent);
}
if (!customElements.get("siblings-element")) {
  customElements.define("siblings-element", SiblingsElement);
}
if (!customElements.get("product-upsell-element")) {
  customElements.define("product-upsell-element", ProductUpsellElement);
}
if (!customElements.get("product-bundle")) {
  customElements.define("product-bundle", ProductBundle);
}
if (!customElements.get("show-subcollection")) {
  customElements.define("show-subcollection", ShowSubcollection);
}
if (!customElements.get("hero-collection-list")) {
  customElements.define("hero-collection-list", HeroCollectionList);
}
if (!customElements.get("hero-media-grid")) {
  customElements.define("hero-media-grid", HeroMediaGrid);
}
if (!customElements.get("subcollection-breadcrumb")) {
  customElements.define("subcollection-breadcrumb", SubcollectionBreadcrumb);
}
const volumeDiscounts = new VolumeDiscounts();
volumeDiscounts.init();
if (!customElements.get("facets-tag-loader")) {
  customElements.define("facets-tag-loader", FacetsTagLoader);
}
if (!customElements.get("more-filters")) {
  customElements.define("more-filters", MoreFiltersElement);
}
if (!customElements.get("facet-toggle")) {
  customElements.define("facet-toggle", class extends DrawerToggleElement {
  });
}
if (!customElements.get("sidebar-toggle")) {
  customElements.define("sidebar-toggle", class extends DrawerToggleElement {
  });
}
if (!customElements.get("facet-toolbar")) {
  customElements.define("facet-toolbar", FacetToolbarElement);
}
if (!customElements.get("facet-remove")) {
  customElements.define("facet-remove", FacetRemoveElement);
}
if (!customElements.get("facet-filters-form")) {
  customElements.define("facet-filters-form", FacetFiltersFormElement);
}
if (!customElements.get("price-slider")) {
  customElements.define("price-slider", PriceSliderElement);
}
if (!customElements.get("filter-product-search")) {
  customElements.define("filter-product-search", FilterProductSearchElement);
}
